import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, forkJoin, interval, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  CrawlService,
  CrawlStatus,
  CrawlEstimate,
  CrawlUrlPreview,
  CrawlUrlPreviewEntry,
  CompetitorReadiness,
  CompetitorStatus,
  DomainProcessingStatus,
  ProcessingJobType,
  SitemapTreeNode,
  LocaleGroup,
  NeverTrackGroup,
  SerpCompetitorSuggestion,
  TopSerpRivalsEstimate,
  TopSerpRivalsStatus,
  SerpRivalKeywordsEstimate,
  SerpRivalKeywordsStatus,
  SetupFocalDomains,
  SetupFocalExclusionReason
} from '../../services/crawl';
import {
  resolveSetupCrawlPhase,
  setupCompetitorChip,
  setupCrawlChip,
  isSetupCrawlDone,
  type SetupCrawlPhase,
  type SetupStatusChip
} from '../../utils/setup-crawl-status';
import {
  addDomainStartCrawlBlockedReason,
  startAllPrepCrawlsBlockedReason,
  type PrepRowGateState
} from '../../utils/setup-start-crawl-gate';
import {
  isNeverTrackGroupTracked,
  neverTrackOverrides,
  pruneNeverTrackOverrides,
  setAllNeverTrackOverrides
} from '../../utils/setup-never-track-selection';
import {
  formatDuration,
  formatUsd,
  rivalCeilingLabel,
  rivalChainActuals,
  rivalChainBlockedReason,
  rivalChainConfirmMessage,
  type RivalChainActuals
} from '../../utils/serp-rival-chain';
import { DomainContextService } from '../../services/domain-context';
import { CompareContextService } from '../../services/compare-context';
import { NotificationService } from '../../services/notification.service';
import { FeatureFlagService } from '../../services/feature-flag';
import { UrlSerpRefreshProgress, UrlSerpSyncService } from '../../services/url-serp-sync';
import {
  CrawlEtaTracker,
  SetupGuide,
  deriveSetupGuide
} from '../../utils/setup-progress-guide';
import { GscConnectionComponent } from '../gsc-connection/gsc-connection';

type SetupStepState =
  | 'no_data'
  | 'not_started'
  | 'waiting'
  | 'queued'
  | 'running'
  | 'partial'
  | 'complete'
  | 'blocked'
  | 'failed';

interface SetupStep {
  key: string;
  title: string;
  description: string;
  /** `process/<endpoint>` suffix; absent for the crawl step. */
  endpoint?: string;
  /** Queue job type used to detect queued/running state. */
  jobType?: ProcessingJobType;
  state: SetupStepState;
  detail: string;
  /** Optional explanatory hint shown under the detail line (not an error). */
  note?: string;
  /**
   * A step that finished but whose result should not be trusted at face value
   * (SS001-10 undercrawl). Distinct from `note` — this one is styled as a warning —
   * and from `state`, because there is nothing left to run.
   */
  warning?: string;
  /** Short badge shown beside the state badge when `warning` is set. */
  warningChip?: string;
  blockedReason?: string | null;
  /** Opt-in steps are excluded from the default Run all + progress. */
  optional?: boolean;
  /** Whether this step exposes a primary Run/Refresh action. */
  actionable: boolean;
  actionLabel: string;
}

/**
 * SS001-10 — a completed crawl that analyzed fewer pages than it scoped.
 * `gap` is ordinary sitemap attrition (redirects, 404s, noindex); `severe` means the
 * analyzed set is a fraction of the site and everything built on it is a sample.
 */
interface CoverageShortfall {
  domain: string;
  severity: 'gap' | 'severe';
  expected: number;
  analyzed: number;
  missing: number;
  /** Analyzed share of expected, 0–100. */
  percent: number;
  /** Up to three missing URLs — usually enough to see the pattern (locale folders). */
  examples: string[];
}

interface PrepDomainRow {
  domain: string;
  role: 'focal' | 'competitor';
  estimate: CrawlEstimate | null;
  estimateLoading: boolean;
  estimateError: string | null;
  crawlStarting: boolean;
  /** Inline leaf/locale/never-track scope under Prep all (expanded after Check all). */
  expanded: boolean;
  selectedSitemaps: Set<string>;
  selectedLocales: Set<string>;
  neverTrackChoices: Map<string, boolean>;
  perSitemapLimits: Record<string, number>;
  perSitemapLimitMode: 'recent' | 'oldest' | 'balanced';
  localePartitionedSitemaps: boolean;
  minLastmodFrom: string;
}

@Component({
  selector: 'app-setup',
  standalone: true,
  imports: [CommonModule, FormsModule, GscConnectionComponent],
  templateUrl: './setup.html',
  styleUrl: './setup.css'
})
export class SetupComponent implements OnInit, OnDestroy {
  domain = '';
  domains: string[] = [];
  /** Domains offered in the focal picker — competitors / support hosts removed (SS001-13/14). */
  setupDomains: string[] = [];
  hiddenSetupDomains: Array<{ domain: string; reason: SetupFocalExclusionReason }> = [];
  showHiddenDomains = false;
  loading = false;
  error: string | null = null;

  /**
   * One crawl status per domain — the single source both the Prep list and the
   * Competitors card read from (SS001-8). Includes the focal domain.
   */
  private crawlStatusByDomain = new Map<string, CrawlStatus | null>();
  /** Focal + competitors whose crawl saw only a fraction of the site (SS001-10). */
  severeUndercrawls: CoverageShortfall[] = [];
  status: DomainProcessingStatus | null = null;

  coreSteps: SetupStep[] = [];
  enrichmentSteps: SetupStep[] = [];
  optionalSteps: SetupStep[] = [];

  runningAll = false;
  busyByKey = new Map<string, boolean>();
  urlSerpForceRefresh = false;
  urlSerpProgress: UrlSerpRefreshProgress | null = null;

  /**
   * Upstream dependencies per step key. A step's Run action is disabled while any
   * of its (transitive) upstream dependencies is queued or running, so you can't
   * fire a step whose input is still being produced.
   */
  private readonly stepDeps: Record<string, string[]> = {
    'links-classify': ['links-backfill'],
    'topics-aggregate': ['topics-map'],
    'internal-links': ['links-classify'],
    'supporting-by-topic': ['topics-aggregate'],
    'supporting-opportunities': ['topics-aggregate']
  };

  // Opt-in toggles (Labs / sidecar). Checked on the prep screen; Start all
  // confirms once, then tryRunPendingFinishOptIns runs them after crawl + core
  // Finish setup. Never part of the automatic free pipeline (SS001-11).
  includeKeywords = false;
  includeUrlSerp = false;
  includeTopSerpRivals = false;
  includeOpportunities = false;
  runningPaidEnrichments = false;

  // Dev-only: scope the workspace-global topics-map step to a subset of domains
  // (focal + competitors) to avoid clustering every domain in the DB.
  topicsMapScope: 'all' | 'selected' = 'all';
  topicsMapDomains = new Set<string>();

  // Competitors (per focal domain).
  competitorStatus: CompetitorStatus | null = null;
  runningCompetitorPrepAll = false;
  competitorBusy = new Map<string, boolean>();
  markCompetitorInput = '';

  // Competitor domains suggested from this domain's tracked-URL SERP snapshots.
  serpSuggestions: SerpCompetitorSuggestion[] = [];
  serpSuggestionsLoading = false;
  serpSuggestionsError: string | null = null;
  serpSuggestionsMinTrackedUrls = 2;

  // Top SERP rivals: one best-ranked rival page per tracked URL, crawled + section-scored.
  topSerpRivals: TopSerpRivalsEstimate | null = null;
  topSerpRivalsLoading = false;
  topSerpRivalsError: string | null = null;
  topSerpRivalsStatus: TopSerpRivalsStatus | null = null;
  topSerpRivalsRunning = false;
  topSerpRivalsExpanded = false;
  topSerpRivalsFailuresExpanded = false;

  // Top SERP rival ranked keywords (opt-in Labs step 2).
  serpRivalKeywords: SerpRivalKeywordsEstimate | null = null;
  serpRivalKeywordsLoading = false;
  serpRivalKeywordsError: string | null = null;
  serpRivalKeywordsStatus: SerpRivalKeywordsStatus | null = null;
  serpRivalKeywordsRunning = false;
  serpRivalKeywordsFailuresExpanded = false;

  /** Batch prep: focal + competitors before Finish setup. */
  prepBatchInput = '';
  prepRows: PrepDomainRow[] = [];
  checkingAllPrep = false;
  prepSitemapCheckDone = 0;
  prepSitemapCheckTotal = 0;
  startingAllPrep = false;
  /**
   * Cold-start builder (empty Setup): focal + optional competitors before any
   * crawl. Becomes the prep list; Check all / Start all then run as today.
   */
  coldStartFocalInput = '';
  coldStartCompetitorsInput = '';
  /**
   * Follow-on steps waiting for their prerequisites (see tryRunPendingFinishOptIns).
   * `pageDemand` is free and rides Finish setup; the paid / sidecar steps are
   * set when Start all confirms the prep-screen opt-ins.
   */
  private pendingFinishOptIns: {
    pageDemand: boolean;
    keywords: boolean;
    urlSerp: boolean;
    topSerpRivals: boolean;
    opportunities: boolean;
  } | null = null;
  private finishOptInGapWarned = false;
  /** SERP snapshot count last used to refresh the rivals cost preview — avoid stale empty quotes. */
  private rivalsPreviewSerpCount: number | null = null;
  /**
   * Page-demand labels are $0 CPU and have always auto-run after Finish setup —
   * but the click that used to schedule them can now happen in the backend
   * (SS001-11), so Setup schedules them off the pipeline state instead. One
   * attempt per domain: a failure must not turn the 6s poll into a retry loop.
   */
  private pageDemandAutoAttemptedFor: string | null = null;

  // Add-domain flow. `addMode` decides whether the crawled domain becomes the
  // new focal selection or is marked as a competitor of the current domain.
  showAddDomain = false;
  addMode: 'focal' | 'competitor' = 'focal';
  newDomainInput = '';
  estimate: CrawlEstimate | null = null;
  estimateLoading = false;
  estimateError: string | null = null;
  startingCrawl = false;
  /** True when remote sitemap discovery was blocked by bot protection. */
  botProtected = false;
  /**
   * The domain the last sitemap check ran for. The Start-crawl gate compares it
   * to the current input so an estimate fetched for one domain can't unlock a
   * crawl of another (SS001-2).
   */
  private checkedDomain: string | null = null;
  /** Uploaded .xml files used instead of remote sitemap fetch. */
  uploadedSitemapFiles: File[] = [];
  usingUploadedSitemaps = false;

  // Sitemap scoping (mirrors Crawl Management granularity).
  readonly defaultCrawlLimit = 750;
  private readonly devCrawlLimit = 10000;
  bypassCrawlCap = false;
  /** Dev-only: use per-URL curated crawl instead of default sitemap mode. */
  usePerUrlFallbackCrawl = false;
  previewUrlsLoading = false;
  localePartitionedSitemaps = false;
  availableLeafSitemaps: Array<{ sitemapUrl: string; urlCount: number }> = [];
  selectedSitemaps = new Set<string>();
  availableLocaleGroups: LocaleGroup[] = [];
  selectedLocales = new Set<string>();
  minLastmodFrom = '';
  /**
   * FW-ONB-01 (SS001-19): only the never-track boxes the operator actually
   * touched. Every other box reads off the estimate's current `defaultExcluded`,
   * so an empty map is exactly the suggested scope.
   */
  neverTrackChoices = new Map<string, boolean>();
  perSitemapLimits: Record<string, number> = {};
  perSitemapLimitMode: 'recent' | 'oldest' | 'balanced' = 'recent';
  private estimateDebounce: ReturnType<typeof setTimeout> | null = null;

/** Set when the operator picks "— New setup —" so resume doesn't undo it (A5). */
  private explicitNewSetup = false;
  /** Live crawl-rate samples behind the "you are here" ETA (SS001-9 / A1). */
  private etaTracker = new CrawlEtaTracker();

  private readonly POLL_MS = 6000;
  private subs = new Subscription();
  private pollSub: Subscription | null = null;
  /** Optional `?domain=` query param to resume an in-progress setup. */
  private routeDomain: string | null = null;

  constructor(
    private crawl: CrawlService,
    private domainContext: DomainContextService,
    private compareContext: CompareContextService,
    private notifications: NotificationService,
    public featureFlags: FeatureFlagService,
    private urlSerpSync: UrlSerpSyncService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.domainContext.ensureInitialized();
    this.subs.add(
      this.domainContext.domains$.subscribe((d) => {
        this.domains = d;
        this.loadSetupDomains();
        this.applyDomainFromRoute();
      })
    );
    this.subs.add(
      this.route.queryParamMap.subscribe((params) => {
        this.routeDomain = params.get('domain');
        this.applyDomainFromRoute();
      })
    );
    if (!this.route.snapshot.queryParamMap.get('domain')) {
      // Resume the domain the rest of the app is already on (SS001-12). Setup
      // used to blank itself on every visit, so returning to a half-finished
      // setup meant re-picking the focal domain to see its progress.
      this.resumeActiveDomain();
    }
    this.startPolling();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.stopPolling();
    if (this.estimateDebounce) clearTimeout(this.estimateDebounce);
  }

  onDomainChange(domain: string): void {
    if (!domain) {
      // Explicit "— New setup —": don't let the resume logic pull the active
      // domain back in on the next domain-list refresh.
      this.explicitNewSetup = true;
      this.clearSetupDomain();
      return;
    }
    this.explicitNewSetup = false;
    this.selectSetupDomain(domain);
  }

  /** Select a focal domain for this Setup session and sync the global active domain. */
  selectSetupDomain(domain: string): void {
    const normalized = this.normalizeDomain(domain);
    if (!normalized) {
      this.clearSetupDomain();
      return;
    }
    this.domain = normalized;
    // Belongs to the domain we just left; loadAll() refills it.
    this.severeUndercrawls = [];
    this.domainContext.setSelectedDomain(normalized);
    this.restorePaidOptInsIntent();
    this.loadAll();
  }

  /**
   * Load the focal-domain picker options. Competitors and support subdomains are
   * filtered server-side; `hidden` keeps them reachable via "Show all".
   */
  private loadSetupDomains(): void {
    const keep = [this.domain, this.domainContext.selectedDomain].filter(Boolean);
    this.crawl.getSetupFocalDomains(keep).subscribe({
      next: (res) => {
        const data: SetupFocalDomains = res.data || { domains: [], hidden: [] };
        this.setupDomains = data.domains || [];
        this.hiddenSetupDomains = data.hidden || [];
        this.resumeActiveDomain();
      },
      error: () => {
        // Fall back to the unfiltered workspace list rather than an empty picker.
        this.setupDomains = this.domains;
        this.hiddenSetupDomains = [];
      }
    });
  }

  /** Picker options, including hidden hosts when the operator asks for them. */
  get pickableDomains(): string[] {
    if (!this.showHiddenDomains) return this.setupDomains;
    return [...new Set([...this.setupDomains, ...this.hiddenSetupDomains.map((h) => h.domain)])].sort();
  }

  hiddenDomainReasonLabel(reason: SetupFocalExclusionReason): string {
    switch (reason) {
      case 'competitor':
        return 'marked competitor';
      case 'subdomain_of_listed_domain':
        return 'subdomain of a listed domain';
      case 'support_subdomain':
        return 'support subdomain';
      default:
        return 'hidden';
    }
  }

  toggleHiddenDomains(): void {
    this.showHiddenDomains = !this.showHiddenDomains;
  }

  /**
   * Select the app's active domain when Setup opens with no explicit `?domain=`
   * and nothing picked yet — but only when it is a valid focal domain.
   */
  private resumeActiveDomain(): void {
    if (this.domain || this.routeDomain || this.explicitNewSetup) return;
    const active = this.normalizeDomain(this.domainContext.selectedDomain || '');
    if (!active || !this.setupDomains.includes(active)) return;
    this.selectSetupDomain(active);
  }

  /** Clear Setup UI state without changing the global active domain elsewhere in the app. */
  clearSetupDomain(): void {
    this.domain = '';
    this.crawlStatusByDomain.clear();
    this.severeUndercrawls = [];
    this.status = null;
    this.coreSteps = [];
    this.enrichmentSteps = [];
    this.optionalSteps = [];
    this.competitorStatus = null;
    this.serpSuggestions = [];
    this.serpSuggestionsError = null;
    this.topSerpRivals = null;
    this.topSerpRivalsStatus = null;
    this.serpRivalKeywords = null;
    this.serpRivalKeywordsStatus = null;
    this.rivalsPreviewSerpCount = null;
    this.prepRows = [];
    this.prepBatchInput = '';
    this.etaTracker.reset();
    this.error = null;
    this.loading = false;
  }

  private applyDomainFromRoute(): void {
    if (!this.routeDomain || this.domains.length === 0) return;
    const wanted = this.normalizeDomain(this.routeDomain);
    if (this.domains.includes(wanted) && this.domain !== wanted) {
      this.selectSetupDomain(wanted);
    }
  }

  // --- Data loading -------------------------------------------------------

  loadAll(showSpinner = true): void {
    if (!this.domain) return;
    if (showSpinner) this.loading = true;
    this.error = null;

    this.refreshCrawlStatusFor(this.domain);

    this.crawl.getProcessingStatus(this.domain).subscribe({
      next: (st) => {
        this.status = st;
        this.rebuildSteps();
        this.tryRunPendingFinishOptIns();
        this.maybeAutoRunPageDemand();
        this.loading = false;
      },
      error: (err) => {
        this.error = err?.error?.message || err?.message || 'Failed to load processing status.';
        this.loading = false;
      }
    });

    this.crawl.getCompetitorStatus(this.domain).subscribe({
      next: (res) => {
        this.competitorStatus = res.data;
        this.syncPrepRowsFromContext();
        this.recomputeSevereUndercrawls();
        // Competitors join the prep list here, so fetch their crawl status now
        // instead of leaving them on the fallback state until the next poll.
        this.refreshPrepRowCrawlStatuses();
      },
      error: () => {
        this.competitorStatus = null;
        this.syncPrepRowsFromContext();
        this.recomputeSevereUndercrawls();
      }
    });

    this.loadSerpSuggestions();
    // Reset the (moderately expensive) rival preview on domain change / manual
    // refresh so it recomputes for the new domain; polls reuse the cached preview
    // unless SERP coverage changed (see loadTopSerpRivals).
    if (showSpinner) {
      this.topSerpRivals = null;
      this.rivalsPreviewSerpCount = null;
    }
    this.loadTopSerpRivals();
    if (showSpinner) this.serpRivalKeywords = null;
    this.loadSerpRivalKeywords();
    this.refreshPrepRowCrawlStatuses();
  }

  /** Load competitor domains suggested from this domain's tracked-URL SERP snapshots. */
  loadSerpSuggestions(): void {
    if (!this.domain) {
      this.serpSuggestions = [];
      return;
    }
    this.serpSuggestionsLoading = true;
    this.serpSuggestionsError = null;
    this.crawl.getSerpCompetitorSuggestions(this.domain).subscribe({
      next: (res) => {
        this.serpSuggestions = res.data?.suggestions || [];
        this.serpSuggestionsMinTrackedUrls = res.data?.minTrackedUrls ?? 2;
        this.serpSuggestionsLoading = false;
      },
      error: (err) => {
        this.serpSuggestions = [];
        this.serpSuggestionsError = err?.error?.message || err?.message || 'Failed to load SERP suggestions.';
        this.serpSuggestionsLoading = false;
      }
    });
  }

  /**
   * Load the Top SERP rivals build status (light, every poll) and the preview
   * (rival picks + cost). Preview refreshes when URL SERP coverage changes so
   * an empty quote from before SERP finished does not stick.
   */
  loadTopSerpRivals(): void {
    if (!this.domain) {
      this.topSerpRivals = null;
      this.topSerpRivalsStatus = null;
      return;
    }
    this.crawl.getTopSerpRivalsStatus(this.domain).subscribe({
      next: (res) => {
        const wasBuilding = this.topSerpRivalsStatus?.status === 'building';
        this.topSerpRivalsStatus = res.data;
        if (wasBuilding && res.data.status === 'ready') {
          // Both previews are stale now: the rivals just crawled are $0 on a
          // re-run, so a quote left over from before the build would overstate
          // the cost of clicking again.
          this.refreshTopSerpRivalsPreview();
          this.refreshSerpRivalKeywordsPreview();
        }
      },
      error: () => undefined
    });

    const serpWith = this.status?.urlSerp?.urlsWithSnapshots ?? 0;
    if (serpWith !== this.rivalsPreviewSerpCount) {
      this.rivalsPreviewSerpCount = serpWith;
      this.refreshTopSerpRivalsPreview();
    } else if (!this.topSerpRivals && !this.topSerpRivalsLoading) {
      this.refreshTopSerpRivalsPreview();
    }
  }

  /** Recompute the rivals cost breakdown (on domain change, or after a build). */
  refreshTopSerpRivalsPreview(): void {
    if (!this.domain) return;
    this.topSerpRivalsLoading = true;
    this.topSerpRivalsError = null;
    this.crawl.getTopSerpRivalsPreview(this.domain).subscribe({
      next: (res) => {
        this.topSerpRivals = res.data;
        this.topSerpRivalsLoading = false;
        this.tryRunPendingFinishOptIns();
      },
      error: (err) => {
        this.topSerpRivals = null;
        this.topSerpRivalsError =
          err?.error?.message || err?.message || 'Failed to load Top SERP rivals.';
        this.topSerpRivalsLoading = false;
      }
    });
  }

  toggleTopSerpRivals(): void {
    this.topSerpRivalsExpanded = !this.topSerpRivalsExpanded;
  }

  /** The visible top-N ceiling line — "capped to N of M", never a silent drop. */
  rivalCeilingLabel(): string | null {
    return rivalCeilingLabel(this.topSerpRivals);
  }

  /** Why the one button is disabled, or null when it can run. */
  rivalChainBlockedReason(): string | null {
    return rivalChainBlockedReason({
      estimate: this.topSerpRivals,
      rivalsStatus: this.topSerpRivalsStatus,
      keywordsStatus: this.serpRivalKeywordsStatus,
      starting: this.topSerpRivalsRunning
    });
  }

  /**
   * Manual escape hatch: confirm here. Start-all opt-in uses startTopSerpRivalsChain
   * with skipConfirm after the prep-screen confirm already covered cost intent.
   */
  runTopSerpRivals(): void {
    if (!this.domain) return;
    const blocked = this.rivalChainBlockedReason();
    if (blocked) {
      this.notifications.error(blocked);
      return;
    }
    const estimate = this.topSerpRivals;
    if (!estimate) return;
    if (!window.confirm(rivalChainConfirmMessage(estimate))) return;
    this.startTopSerpRivalsChain();
  }

  /** Kick off crawl → rival keywords. Confirm is the caller's job when skipConfirm. */
  private startTopSerpRivalsChain(): void {
    if (!this.domain || !this.topSerpRivals) return;
    this.topSerpRivalsRunning = true;
    this.crawl.runTopSerpRivals(this.domain, { chainKeywords: true }).subscribe({
      next: (res) => {
        this.topSerpRivalsStatus = res.data.status;
        this.topSerpRivalsRunning = false;
        this.notifications.success(
          'Started — crawling rival pages, then fetching their ranked keywords. No further clicks needed.'
        );
      },
      error: (err) => {
        this.topSerpRivalsRunning = false;
        this.notifications.error(
          err?.error?.message || err?.message || 'Failed to start Top SERP rivals build.'
        );
      }
    });
  }

  /** Checking rivals implies URL SERP (rivals need snapshots). */
  onIncludeTopSerpRivalsChange(checked: boolean): void {
    if (checked) this.includeUrlSerp = true;
  }

  /**
   * SS001-7: what the run that just finished actually took and actually cost,
   * across both legs of the chain.
   */
  rivalChainActuals(): RivalChainActuals {
    return rivalChainActuals(this.topSerpRivalsStatus, this.serpRivalKeywordsStatus);
  }

  /** Template access to the shared $ / duration formatting. */
  formatChainUsd(value: number): string {
    return formatUsd(value);
  }

  formatChainDuration(ms: number | null): string | null {
    return formatDuration(ms);
  }

  /** True while the keyword leg is waiting on the crawl it was chained behind. */
  serpRivalKeywordsQueuedBehindCrawl(): boolean {
    return (
      this.topSerpRivalsBuilding()
      && !!this.serpRivalKeywordsStatus?.chained
      && this.serpRivalKeywordsStatus.status === 'not_started'
    );
  }

  topSerpRivalsBuilding(): boolean {
    return this.topSerpRivalsStatus?.status === 'building';
  }

  toggleTopSerpRivalsFailures(): void {
    this.topSerpRivalsFailuresExpanded = !this.topSerpRivalsFailuresExpanded;
  }

  topSerpRivalsFailureCount(): number {
    return this.topSerpRivalsStatus?.failures?.length ?? 0;
  }

  topSerpRivalsSuccessCount(): number {
    const s = this.topSerpRivalsStatus;
    if (!s) return 0;
    return Math.max(0, s.evaluatedUrls - this.topSerpRivalsFailureCount());
  }

  /** Combined crawl + eval progress (0–100) from the backend. */
  topSerpRivalsProgressPct(): number {
    const s = this.topSerpRivalsStatus;
    if (!s) return 0;
    return s.progressPercent ?? 0;
  }

  topSerpRivalsStatusLine(): string {
    const s = this.topSerpRivalsStatus;
    if (!s || s.status !== 'building') return '';

    const eta = s.estimatedRemainingLabel ? ` · ${s.estimatedRemainingLabel}` : '';
    if (s.phase === 'evaluating') {
      return `Computing section coverage ${s.evaluatedUrls}/${s.totalUrls}${eta}`;
    }
    return `Crawling rival pages ${s.crawledRivals}/${s.totalRivals}${eta}`;
  }

  /** Setup status: how many SERP queries/pages already have answer-structure LLM cached. */
  answerStructureStatusLine(): string | null {
    const as = this.status?.urlSerp?.answerStructure;
    if (!as || as.urlsWithSerp <= 0) return null;
    if (as.uniqueKeywords <= 0) return null;
    const missing = Math.max(0, as.uniqueKeywords - as.cachedKeywords);
    if (missing === 0) {
      return `Answer structure LLM ready for ${as.urlsWithCachedStructure} page(s) · ${as.cachedKeywords} unique SERP queries cached.`;
    }
    return `Answer structure LLM ${as.cachedKeywords} / ${as.uniqueKeywords} unique SERP queries (${as.urlsWithCachedStructure} / ${as.urlsWithSerp} pages) — re-run URL SERP snapshots to warm the rest.`;
  }

  loadSerpRivalKeywords(): void {
    if (!this.domain) {
      this.serpRivalKeywords = null;
      this.serpRivalKeywordsStatus = null;
      return;
    }
    this.crawl.getSerpRivalKeywordsStatus(this.domain).subscribe({
      next: (res) => (this.serpRivalKeywordsStatus = res.data),
      error: () => undefined
    });

    if (!this.serpRivalKeywords && !this.serpRivalKeywordsLoading) {
      this.refreshSerpRivalKeywordsPreview();
    }
  }

  /** Recompute keyword preview (e.g. after step 1 crawl finishes). */
  refreshSerpRivalKeywordsPreview(): void {
    if (!this.domain) return;
    this.serpRivalKeywordsLoading = true;
    this.serpRivalKeywordsError = null;
    this.crawl.getSerpRivalKeywordsPreview(this.domain).subscribe({
      next: (res) => {
        this.serpRivalKeywords = res.data;
        this.serpRivalKeywordsLoading = false;
      },
      error: (err) => {
        this.serpRivalKeywords = null;
        this.serpRivalKeywordsError =
          err?.error?.message || err?.message || 'Failed to load rival keyword preview.';
        this.serpRivalKeywordsLoading = false;
      }
    });
  }

  serpRivalKeywordsCanRun(): boolean {
    if (!this.serpRivalKeywords) return false;
    if (this.serpRivalKeywordsRunning || this.serpRivalKeywordsBuilding()) return false;
    if (this.topSerpRivalsBuilding()) return false;
    return this.serpRivalKeywords.needsFetchCount > 0;
  }

  /**
   * Escape hatch, not step 2 of a wizard: the chain already runs this leg. It
   * exists for a re-fetch after a partial failure, and still bills, so it keeps
   * its own confirm with its own numbers.
   */
  runSerpRivalKeywords(): void {
    if (!this.domain || !this.serpRivalKeywordsCanRun()) return;
    const kw = this.serpRivalKeywords!;
    const confirmed = window.confirm(
      `Re-fetch rival ranked keywords now?\n\n`
        + `${kw.needsFetchCount.toLocaleString('en-US')} rival page(s) × ${formatUsd(kw.estimatedCostPerUrl)}`
        + ` = ${formatUsd(kw.estimatedCost)} (DataForSEO Labs).\n\n`
        + 'The rivals chain normally runs this for you — this is a re-run for pages it missed.'
    );
    if (!confirmed) return;

    this.serpRivalKeywordsRunning = true;
    this.crawl.runSerpRivalKeywords(this.domain).subscribe({
      next: (res) => {
        this.serpRivalKeywordsStatus = res.data.status;
        this.serpRivalKeywordsRunning = false;
        this.notifications.success('Rival keyword fetch started.');
      },
      error: (err) => {
        this.serpRivalKeywordsRunning = false;
        this.notifications.error(
          err?.error?.message || err?.message || 'Failed to start rival keyword fetch.'
        );
      }
    });
  }

  serpRivalKeywordsBuilding(): boolean {
    return this.serpRivalKeywordsStatus?.status === 'building';
  }

  serpRivalKeywordsProgressPct(): number {
    return this.serpRivalKeywordsStatus?.progressPercent ?? 0;
  }

  serpRivalKeywordsStatusLine(): string {
    const s = this.serpRivalKeywordsStatus;
    if (!s || s.status !== 'building') return '';
    const eta = s.estimatedRemainingLabel ? ` · ${s.estimatedRemainingLabel}` : '';
    return `Fetching ranked keywords ${s.fetchedUrls + s.failedUrls}/${s.totalUrls}${eta}`;
  }

  serpRivalKeywordsFailureCount(): number {
    return this.serpRivalKeywordsStatus?.failures?.length ?? 0;
  }

  toggleSerpRivalKeywordsFailures(): void {
    this.serpRivalKeywordsFailuresExpanded = !this.serpRivalKeywordsFailuresExpanded;
  }

  formatRivalUrl(url: string): string {
    try {
      const parsed = new URL(url);
      return `${parsed.hostname.replace(/^www\./, '')}${parsed.pathname === '/' ? '' : parsed.pathname}`;
    } catch {
      return url;
    }
  }

  private refreshPrepRowCrawlStatuses(): void {
    for (const row of this.prepRows) {
      if (row.domain === this.domain) continue; // already refreshed with the focal load (A5)
      this.refreshCrawlStatusFor(row.domain);
    }
  }

  private refreshCrawlStatusFor(domain: string): void {
    const key = this.normalizeDomain(domain);
    if (!key) return;
    this.crawl.getCrawlStatus(key).subscribe({
      next: (cs) => {
        this.crawlStatusByDomain.set(key, cs);
        this.etaTracker.observe(key, cs);
        this.recomputeSevereUndercrawls();
      },
      error: () => {
        this.crawlStatusByDomain.set(key, null);
        this.etaTracker.forget(key);
        this.recomputeSevereUndercrawls();
      }
    });
  }

  /** Live crawl status for a domain — the one source every Setup card reads. */
  crawlStatusFor(domain: string): CrawlStatus | null {
    return this.crawlStatusByDomain.get(this.normalizeDomain(domain)) ?? null;
  }

  /** Crawl status of the focal domain (template + pipeline gates). */
  get crawlStatus(): CrawlStatus | null {
    return this.domain ? this.crawlStatusFor(this.domain) : null;
  }

  // --- Undercrawl (SS001-10) ----------------------------------------------

  /**
   * A finished crawl's analyzed/expected shortfall, shaped for display.
   *
   * The severity is the backend's call (`assessCrawlCoverage`) — a threshold with two
   * owners is a threshold that drifts. Null while a crawl is still running, where
   * "missing" only ever means "not yet".
   */
  coverageShortfallFor(domain: string): CoverageShortfall | null {
    const cs = this.crawlStatusFor(domain);
    const cov = cs?.coverage;
    if (!cs || !cov || cov.expected <= 0) return null;
    if (!cs.isComplete && cs.phase !== 'complete') return null;

    const analyzed = Math.min(cov.analyzed, cov.expected);
    const missing = cov.expected - analyzed;
    if (missing <= 0) return null;

    return {
      domain,
      severity: cov.severity === 'severe' ? 'severe' : 'gap',
      expected: cov.expected,
      analyzed,
      missing,
      percent: Math.round((analyzed / cov.expected) * 100),
      examples: (cov.missingUrls || []).slice(0, 3)
    };
  }

  /** Shortfall for a domain, only when it is severe — the template's guard. */
  severeShortfallFor(domain: string): CoverageShortfall | null {
    const shortfall = this.coverageShortfallFor(domain);
    return shortfall?.severity === 'severe' ? shortfall : null;
  }

  /** Focal-domain shortfall, for the progress card and the Analyze step. */
  get coverageShortfall(): CoverageShortfall | null {
    return this.domain ? this.coverageShortfallFor(this.domain) : null;
  }

  /**
   * Every domain feeding this focal's comparisons — itself and its competitors — whose
   * crawl only saw a fraction of the site. This is the SS001-10 ask: the operator must
   * see that a competitor's topic set is a sample before trusting a supporting-page
   * comparison against it.
   *
   * Recomputed on the events that can change it rather than exposed as a getter: it
   * feeds an `*ngFor`, and a getter handing back a fresh array every change-detection
   * pass makes Angular tear down and rebuild those rows on every tick.
   */
  private recomputeSevereUndercrawls(): void {
    this.severeUndercrawls = this.collectSevereUndercrawls();
  }

  private collectSevereUndercrawls(): CoverageShortfall[] {
    if (!this.domain) return [];
    const focal = this.normalizeDomain(this.domain);
    const domains = [focal, ...this.competitors.map((c) => this.normalizeDomain(c.domain))];
    const seen = new Set<string>();
    const out: CoverageShortfall[] = [];
    for (const d of domains) {
      if (!d || seen.has(d)) continue;
      seen.add(d);
      const shortfall = this.coverageShortfallFor(d);
      if (shortfall?.severity === 'severe') out.push(shortfall);
    }
    return out;
  }

  /**
   * `406 of 1,123 pages analyzed (36%)` — the one phrasing every severe-undercrawl line
   * uses. Severe only: on a 502/503 gap the percentage rounds to 100 and reads as a
   * contradiction, so `gap` copy states the counts without it.
   */
  undercrawlCounts(s: CoverageShortfall): string {
    return `${s.analyzed.toLocaleString()} of ${s.expected.toLocaleString()} pages analyzed (${s.percent}%)`;
  }

  /**
   * Single crawl phase for a domain: live status first, the backend's competitor
   * `crawlState` as fallback until it loads (SS001-8).
   */
  crawlPhaseFor(domain: string, starting = false): SetupCrawlPhase {
    const key = this.normalizeDomain(domain);
    const live = this.crawlStatusByDomain.get(key) ?? null;
    const reported = (this.competitorStatus?.competitors || []).find(
      (c) => this.normalizeDomain(c.domain) === key
    )?.crawlState;
    return resolveSetupCrawlPhase({ starting, live, reported: reported ?? null });
  }

  /** True once a domain's crawl + analysis have finished. */
  crawlDoneFor(domain: string): boolean {
    return isSetupCrawlDone(this.crawlPhaseFor(domain));
  }

  private emptyPrepRow(domain: string, role: 'focal' | 'competitor'): PrepDomainRow {
    return {
      domain,
      role,
      estimate: null,
      estimateLoading: false,
      estimateError: null,
      crawlStarting: false,
      expanded: false,
      selectedSitemaps: new Set(),
      selectedLocales: new Set(),
      neverTrackChoices: new Map(),
      perSitemapLimits: {},
      perSitemapLimitMode: 'recent',
      localePartitionedSitemaps: false,
      minLastmodFrom: ''
    };
  }

  private syncPrepRowsFromContext(): void {
    if (!this.domain) {
      this.prepRows = [];
      return;
    }
    const focal = this.normalizeDomain(this.domain);
    const competitorDomains = (this.competitorStatus?.competitors || []).map((c) => c.domain);
    const fromContext = [focal, ...competitorDomains.filter((d) => d !== focal)];
    const existing = new Map(this.prepRows.map((r) => [r.domain, r]));

    const synced: PrepDomainRow[] = [];
    const seen = new Set<string>();

    for (const d of fromContext) {
      seen.add(d);
      const prev = existing.get(d);
      if (prev) {
        prev.role = d === focal ? 'focal' : 'competitor';
        synced.push(prev);
      } else {
        synced.push(this.emptyPrepRow(d, d === focal ? 'focal' : 'competitor'));
      }
    }

    // Keep batch-added prep rows until the operator marks them as competitors or removes them.
    for (const row of this.prepRows) {
      if (seen.has(row.domain)) continue;
      seen.add(row.domain);
      synced.push(row);
    }

    this.prepRows = synced;
  }

  addPrepDomainsFromBatchInput(): void {
    if (!this.domain) return;
    const focal = this.normalizeDomain(this.domain);
    const lines = this.prepBatchInput
      .split(/[\n,;]+/)
      .map((s) => this.normalizeDomain(s))
      .filter(Boolean);
    const existing = new Set(this.prepRows.map((r) => r.domain));
    for (const d of lines) {
      if (d === focal || existing.has(d)) continue;
      this.prepRows.push(this.emptyPrepRow(d, 'competitor'));
      existing.add(d);
      this.refreshCrawlStatusFor(d);
    }
    this.prepBatchInput = '';
  }

  /**
   * Empty-Setup entry: focal (+ optional competitors) → prep list → Check all /
   * Start all. Avoids the crawl-first trap where competitors only appear below
   * Step 4 after a solo focal crawl.
   */
  buildColdStartPrepList(): void {
    const focal = this.normalizeDomain(this.coldStartFocalInput);
    if (!focal) {
      this.notifications.error('Enter a focal domain (e.g. example.com).');
      return;
    }
    const competitors = [
      ...new Set(
        this.coldStartCompetitorsInput
          .split(/[\n,;]+/)
          .map((s) => this.normalizeDomain(s))
          .filter((d) => d && d !== focal)
      )
    ];

    const rows: PrepDomainRow[] = [
      this.emptyPrepRow(focal, 'focal'),
      ...competitors.map((d) => this.emptyPrepRow(d, 'competitor'))
    ];
    // Set before selectSetupDomain so loadAll → syncPrepRowsFromContext keeps
    // competitors that are not yet in WorkspaceCompareSettings.
    this.prepRows = rows;
    this.explicitNewSetup = false;
    this.showAddDomain = false;
    this.selectSetupDomain(focal);
    for (const row of rows) {
      this.refreshCrawlStatusFor(row.domain);
    }
    this.coldStartFocalInput = '';
    this.coldStartCompetitorsInput = '';

    this.notifications.success(
      competitors.length
        ? `Prep list ready: ${focal} + ${competitors.length} competitor(s). Check sitemaps, then Start all crawls.`
        : `Prep list ready: ${focal}. Add competitors below, or Check sitemaps and Start all crawls.`
    );
    setTimeout(() => {
      document.getElementById('setup-prep-all')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  }

  private prepRowNeedsSitemapCheck(row: PrepDomainRow): boolean {
    if (row.estimate) return false;
    if (row.crawlStarting) return false;
    const phase = this.crawlPhaseFor(row.domain);
    // A crawl already in flight can't be re-scoped, so don't spend an estimate on it.
    return phase !== 'crawling' && phase !== 'analyzing';
  }

  checkAllPrepSitemaps(): void {
    if (this.prepRows.length === 0 || this.checkingAllPrep) return;

    const rowsToCheck = this.prepRows.filter((row) => this.prepRowNeedsSitemapCheck(row));
    const skipped = this.prepRows.length - rowsToCheck.length;

    if (rowsToCheck.length === 0) {
      this.notifications.info(
        skipped > 0
          ? `Skipped all ${skipped} domain(s) — already checked or crawl in progress.`
          : 'No domains to check.'
      );
      return;
    }

    this.checkingAllPrep = true;
    this.prepSitemapCheckDone = 0;
    this.prepSitemapCheckTotal = rowsToCheck.length;

    const rows = [...rowsToCheck];
    for (const row of rows) {
      row.estimateLoading = true;
      row.estimateError = null;
    }

    if (skipped > 0) {
      this.notifications.info(`Checking ${rowsToCheck.length} domain(s); skipped ${skipped} already checked or crawling.`);
    }

    const CONCURRENCY = 2;
    let nextIndex = 0;

    const runBatch = (): void => {
      const batch: PrepDomainRow[] = [];
      while (batch.length < CONCURRENCY && nextIndex < rows.length) {
        batch.push(rows[nextIndex++]);
      }
      if (batch.length === 0) {
        this.checkingAllPrep = false;
        return;
      }

      const requests = batch.map((row) =>
        this.crawl.estimateCrawl(row.domain, {
          limit: this.getEffectiveCrawlLimit()
        }).pipe(
          map((res) => ({ row, est: res.data, error: null as string | null })),
          catchError((err) =>
            of({
              row,
              est: null,
              error: err?.error?.error || err?.error?.message || err?.message || 'Estimate failed'
            })
          )
        )
      );

      forkJoin(requests).subscribe({
        next: (results) => {
          for (const r of results) {
            r.row.estimateLoading = false;
            this.prepSitemapCheckDone += 1;
            if (r.error || !r.est) {
              r.row.estimateError = r.error || 'Estimate failed';
              r.row.estimate = null;
            } else {
              this.applyPrepRowEstimate(r.row, r.est, false);
            }
          }
          runBatch();
        },
        error: () => {
          for (const row of batch) {
            row.estimateLoading = false;
            row.estimateError = row.estimateError || 'Estimate failed';
            this.prepSitemapCheckDone += 1;
          }
          runBatch();
        }
      });
    };

    runBatch();
  }

  /**
   * Apply an estimate onto a prep row and (re)seed leaf/locale/never-track
   * selections. Mirrors `applyEstimateResponse` for the Add domain panel.
   */
  private applyPrepRowEstimate(row: PrepDomainRow, data: CrawlEstimate, preserveSelection: boolean): void {
    row.estimate = data;
    row.estimateError = null;
    row.localePartitionedSitemaps = !!data.localePartitionedSitemaps;

    const leaves = this.collectLeafSitemaps(data?.sitemapTree || []);
    const validLeafSet = new Set(leaves.map((e) => e.sitemapUrl));
    row.perSitemapLimits = Object.fromEntries(
      Object.entries(row.perSitemapLimits).filter(([url, v]) => validLeafSet.has(url) && v > 0)
    );

    const groups = data?.neverTrackGroups || [];
    row.neverTrackChoices = preserveSelection
      ? pruneNeverTrackOverrides(groups, row.neverTrackChoices)
      : new Map();

    const localeGroups = data?.localeGroups || [];
    const validLocaleSet = new Set(localeGroups.map((g) => g.key));
    if (preserveSelection) {
      row.selectedLocales = new Set(
        Array.from(row.selectedLocales).filter((key) => validLocaleSet.has(key))
      );
    } else if (localeGroups.length > 0) {
      row.selectedLocales = new Set(validLocaleSet.has('__default__') ? ['__default__'] : []);
    } else {
      row.selectedLocales = new Set();
    }

    if (preserveSelection) {
      row.selectedSitemaps = new Set(
        Array.from(row.selectedSitemaps).filter((url) => validLeafSet.has(url))
      );
    } else if (row.localePartitionedSitemaps) {
      this.syncPrepRowSitemapsToLocales(row);
    } else {
      row.selectedSitemaps = new Set(leaves.map((e) => e.sitemapUrl));
    }

    row.expanded = true;
    row.estimateLoading = false;

    // First check: re-estimate with default locale selection so counts match Scope.
    if (!preserveSelection && localeGroups.length > 0 && row.selectedLocales.size > 0) {
      this.refreshPrepRowEstimate(row);
    }
  }

  private syncPrepRowSitemapsToLocales(row: PrepDomainRow): void {
    if (!row.localePartitionedSitemaps) return;
    const leaves = this.getLeafNodes(row.estimate?.sitemapTree);
    row.selectedSitemaps = new Set(
      leaves
        .filter((leaf) => {
          const key = leaf.dominantLocaleKey || '__default__';
          return row.selectedLocales.has(key);
        })
        .map((leaf) => leaf.sitemapUrl)
    );
  }

  private prepRowNeverTrackKeys(row: PrepDomainRow): {
    keepNeverTrackKeys: string[];
    excludeNeverTrackKeys: string[];
  } {
    return neverTrackOverrides(row.estimate?.neverTrackGroups || [], row.neverTrackChoices);
  }

  private buildPrepRowEstimateOptions(row: PrepDomainRow, preserveSelection: boolean) {
    const nt = this.prepRowNeverTrackKeys(row);
    return {
      limit: this.getEffectiveCrawlLimit(),
      selectedSitemaps: preserveSelection ? Array.from(row.selectedSitemaps) : [],
      selectedLocales: preserveSelection ? Array.from(row.selectedLocales) : [],
      minLastmodFrom: row.minLastmodFrom || null,
      perSitemapLimits: row.perSitemapLimits,
      perSitemapLimitMode: row.perSitemapLimitMode,
      keepNeverTrackKeys: preserveSelection ? nt.keepNeverTrackKeys : [],
      excludeNeverTrackKeys: preserveSelection ? nt.excludeNeverTrackKeys : []
    };
  }

  refreshPrepRowEstimate(row: PrepDomainRow): void {
    if (!row.domain || row.estimateLoading) return;
    row.estimateLoading = true;
    row.estimateError = null;
    this.crawl.estimateCrawl(row.domain, this.buildPrepRowEstimateOptions(row, true)).subscribe({
      next: (res) => this.applyPrepRowEstimate(row, res.data, true),
      error: (err) => {
        row.estimateLoading = false;
        row.estimateError =
          err?.error?.error || err?.error?.message || err?.message || 'Estimate failed';
      }
    });
  }

  togglePrepRowExpanded(row: PrepDomainRow): void {
    row.expanded = !row.expanded;
  }

  isPrepNeverTrackTracked(row: PrepDomainRow, group: NeverTrackGroup): boolean {
    return isNeverTrackGroupTracked(group, row.neverTrackChoices);
  }

  togglePrepNeverTrack(row: PrepDomainRow, group: NeverTrackGroup, tracked: boolean): void {
    row.neverTrackChoices.set(group.key, tracked);
    this.refreshPrepRowEstimate(row);
  }

  setAllPrepNeverTrackTracked(row: PrepDomainRow, tracked: boolean): void {
    row.neverTrackChoices = setAllNeverTrackOverrides(row.estimate?.neverTrackGroups || [], tracked);
    this.refreshPrepRowEstimate(row);
  }

  prepNeverTrackExcludedCount(row: PrepDomainRow): number {
    return row.estimate?.neverTrackExcludedCount ?? 0;
  }

  getPrepLeafDisplayUrlCount(row: PrepDomainRow, node: SitemapTreeNode): number {
    const base = row.minLastmodFrom ? (node.filteredUrlCount ?? 0) : (node.urlCount || 0);
    const cap = this.getPrepPerSitemapLimit(row, node.sitemapUrl);
    return cap ? Math.min(base, cap) : base;
  }

  getPrepPerSitemapLimit(row: PrepDomainRow, sitemapUrl: string): number | null {
    const value = row.perSitemapLimits[sitemapUrl];
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  onPrepPerSitemapLimitChange(row: PrepDomainRow, sitemapUrl: string, value: string | number | null): void {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) {
      row.perSitemapLimits[sitemapUrl] = Math.floor(parsed);
    } else {
      delete row.perSitemapLimits[sitemapUrl];
    }
    this.refreshPrepRowEstimate(row);
  }

  onPrepMinLastmodFromChange(row: PrepDomainRow, value: string): void {
    row.minLastmodFrom = value;
    this.refreshPrepRowEstimate(row);
  }

  onPrepPerSitemapLimitModeChange(row: PrepDomainRow, value: 'recent' | 'oldest' | 'balanced'): void {
    row.perSitemapLimitMode = value;
    this.refreshPrepRowEstimate(row);
  }

  togglePrepSitemap(row: PrepDomainRow, sitemapUrl: string, checked: boolean): void {
    if (checked) row.selectedSitemaps.add(sitemapUrl);
    else row.selectedSitemaps.delete(sitemapUrl);
    this.refreshPrepRowEstimate(row);
  }

  setAllPrepSitemapsSelected(row: PrepDomainRow, checked: boolean): void {
    const leaves = this.collectLeafSitemaps(row.estimate?.sitemapTree || []);
    row.selectedSitemaps = checked ? new Set(leaves.map((e) => e.sitemapUrl)) : new Set();
    this.refreshPrepRowEstimate(row);
  }

  areAllPrepSitemapsSelected(row: PrepDomainRow): boolean {
    const leaves = this.collectLeafSitemaps(row.estimate?.sitemapTree || []);
    return leaves.length > 0 && row.selectedSitemaps.size === leaves.length;
  }

  togglePrepLocale(row: PrepDomainRow, key: string, checked: boolean): void {
    if (checked) row.selectedLocales.add(key);
    else row.selectedLocales.delete(key);
    if (row.localePartitionedSitemaps) this.syncPrepRowSitemapsToLocales(row);
    this.refreshPrepRowEstimate(row);
  }

  setAllPrepLocalesSelected(row: PrepDomainRow, checked: boolean): void {
    const groups = row.estimate?.localeGroups || [];
    row.selectedLocales = checked ? new Set(groups.map((g) => g.key)) : new Set();
    if (row.localePartitionedSitemaps) this.syncPrepRowSitemapsToLocales(row);
    this.refreshPrepRowEstimate(row);
  }

  areAllPrepLocalesSelected(row: PrepDomainRow): boolean {
    const groups = row.estimate?.localeGroups || [];
    return groups.length > 0 && row.selectedLocales.size === groups.length;
  }

  prepRowChip(row: PrepDomainRow): SetupStatusChip {
    return setupCrawlChip(this.crawlPhaseFor(row.domain, row.crawlStarting));
  }

  openPrepRowScope(row: PrepDomainRow): void {
    this.addMode = row.role === 'focal' ? 'focal' : 'competitor';
    this.newDomainInput = row.domain;
    this.showAddDomain = true;
    this.estimateNewDomain();
    setTimeout(() => {
      const el = document.getElementById('setup-add-panel');
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  }

  /** Gate rows for Start all crawls — mirrors what the batch would actually start. */
  private prepGateRows(): PrepRowGateState[] {
    return this.prepRows.map((row) => ({
      domain: row.domain,
      crawlDone: this.crawlDoneFor(row.domain),
      hasEstimate: Boolean(row.estimate),
      hasEstimateError: Boolean(row.estimateError)
    }));
  }

  /** Why Start all crawls is disabled — sitemap check first (SS001-2). */
  get startAllPrepBlockedReason(): string | null {
    return startAllPrepCrawlsBlockedReason({
      rows: this.prepGateRows(),
      startingAll: this.startingAllPrep,
      checkingAll: this.checkingAllPrep
    });
  }

  get canStartAllPrepCrawls(): boolean {
    return this.startAllPrepBlockedReason === null;
  }

  /**
   * Spend confirm at Start all (GO_LIVE_PLAN Track A). Checking boxes on prep
   * only declares intent; this dialog is the one click that may bill Labs.
   */
  private confirmPaidEnrichmentsAtStart(): boolean {
    const steps = this.selectedPaidEnrichments;
    if (steps.length === 0) return true;
    return window.confirm(
      `Start crawls, then run these after core setup finishes?\n\n`
        + `${steps.map((s) => `• ${s}`).join('\n')}\n\n`
        + 'DataForSEO Labs steps are billed per URL. Supporting-page opportunities need the content-gap sidecar. '
        + 'Nothing paid starts until you confirm here.'
    );
  }

  private paidOptInsStorageKey(domain: string): string {
    return `taptimize.setupPaidOptIns.${this.normalizeDomain(domain)}`;
  }

  /** Survive a refresh while crawls / Finish setup are still running. */
  private persistPaidOptInsIntent(): void {
    if (!this.domain || typeof sessionStorage === 'undefined') return;
    const key = this.paidOptInsStorageKey(this.domain);
    if (
      !this.includeKeywords
      && !this.includeUrlSerp
      && !this.includeTopSerpRivals
      && !this.includeOpportunities
    ) {
      sessionStorage.removeItem(key);
      return;
    }
    sessionStorage.setItem(
      key,
      JSON.stringify({
        keywords: this.includeKeywords,
        urlSerp: this.includeUrlSerp,
        topSerpRivals: this.includeTopSerpRivals,
        opportunities: this.includeOpportunities
      })
    );
  }

  private clearPersistedPaidOptInsIntent(): void {
    if (!this.domain || typeof sessionStorage === 'undefined') return;
    sessionStorage.removeItem(this.paidOptInsStorageKey(this.domain));
  }

  private restorePaidOptInsIntent(): void {
    if (!this.domain || typeof sessionStorage === 'undefined') return;
    try {
      const raw = sessionStorage.getItem(this.paidOptInsStorageKey(this.domain));
      if (!raw) return;
      const parsed = JSON.parse(raw) as {
        keywords?: boolean;
        urlSerp?: boolean;
        topSerpRivals?: boolean;
        opportunities?: boolean;
      };
      this.includeKeywords = Boolean(parsed.keywords);
      this.includeUrlSerp = Boolean(parsed.urlSerp);
      this.includeTopSerpRivals = Boolean(parsed.topSerpRivals);
      this.includeOpportunities = Boolean(parsed.opportunities);
      if (this.includeTopSerpRivals) this.includeUrlSerp = true;
      if (
        !this.includeKeywords
        && !this.includeUrlSerp
        && !this.includeTopSerpRivals
        && !this.includeOpportunities
      ) {
        return;
      }
      this.queuePendingOptIns({
        pageDemand: this.includeKeywords || this.includeUrlSerp || this.includeTopSerpRivals,
        keywords: this.includeKeywords,
        urlSerp: this.includeUrlSerp || this.includeTopSerpRivals,
        topSerpRivals: this.includeTopSerpRivals,
        opportunities: this.includeOpportunities
      });
    } catch {
      this.clearPersistedPaidOptInsIntent();
    }
  }

  get hasPendingPaidEnrichments(): boolean {
    const p = this.pendingFinishOptIns;
    if (!p) return false;
    return Boolean(p.keywords || p.urlSerp || p.topSerpRivals || p.opportunities || p.pageDemand);
  }

  startAllPrepCrawls(): void {
    if (!this.domain || !this.canStartAllPrepCrawls) return;
    if (!this.confirmPaidEnrichmentsAtStart()) return;

    if (this.selectedPaidEnrichments.length > 0) {
      this.finishOptInGapWarned = false;
      this.queuePendingOptIns({
        pageDemand: this.includeKeywords || this.includeUrlSerp || this.includeTopSerpRivals,
        keywords: this.includeKeywords,
        urlSerp: this.includeUrlSerp || this.includeTopSerpRivals,
        topSerpRivals: this.includeTopSerpRivals,
        opportunities: this.includeOpportunities
      });
      this.persistPaidOptInsIntent();
      this.notifications.success(
        'Paid enrichments confirmed — they start after crawl and core setup finish.'
      );
    }

    this.startingAllPrep = true;
    const focalDomain = this.domain;
    const toStart = this.prepRows.filter((r) => !this.crawlDoneFor(r.domain));

    const startOne = (row: PrepDomainRow) => {
      row.crawlStarting = true;
      this.showAddDomain = false;
      const nt = this.prepRowNeverTrackKeys(row);
      const opts = {
        limit: this.getEffectiveCrawlLimit(),
        crawlMode: (this.isDevView() && this.usePerUrlFallbackCrawl ? 'curated' : 'sitemap') as 'sitemap' | 'curated',
        selectedSitemaps: Array.from(row.selectedSitemaps),
        selectedLocales: Array.from(row.selectedLocales),
        minLastmodFrom: row.minLastmodFrom || null,
        perSitemapLimits: row.perSitemapLimits,
        perSitemapLimitMode: row.perSitemapLimitMode,
        keepNeverTrackKeys: nt.keepNeverTrackKeys,
        excludeNeverTrackKeys: nt.excludeNeverTrackKeys
      };
      return this.crawl.startCrawl(row.domain, opts).pipe(
        map((res) => ({ row, res, error: null as string | null })),
        catchError((err) =>
          of({
            row,
            res: null,
            error: err?.error?.error || err?.error?.message || err?.message || 'Failed to start crawl'
          })
        )
      );
    };

    const afterStart = (row: PrepDomainRow, error: string | null) => {
      row.crawlStarting = false;
      if (error) {
        this.notifications.error(`${row.domain}: ${error}`);
        return;
      }
      if (row.role === 'competitor') {
        this.compareContext.addMarkedCompetitor(row.domain, focalDomain).subscribe({
          next: () => this.loadAll(false),
          error: (err) =>
            this.notifications.error(
              err?.error?.error || err?.error?.message || `Crawl started but failed to mark ${row.domain} as competitor.`
            )
        });
      }
    };

    forkJoin(toStart.map((row) => startOne(row))).subscribe({
      next: (results) => {
        for (const r of results) {
          afterStart(r.row, r.error);
          if (!r.error) {
            this.notifications.success(r.res?.message || `Crawl started for ${r.row.domain}.`);
          }
        }
        this.startingAllPrep = false;
        this.loadAll(false);
      },
      error: () => {
        this.startingAllPrep = false;
      }
    });
  }

  get allCompetitorsCrawled(): boolean {
    const comps = this.competitors;
    if (comps.length === 0) return true;
    return comps.every((c) => c.crawled);
  }

  /**
   * Every domain in the prep set — focal included — has finished crawl + analyze.
   * This is the same gate the backend auto-enqueue uses (SS001-9), so the note
   * below never promises "prep queues itself" while the focal crawl is still
   * running and the gate is in fact closed.
   */
  get allPrepCrawlsComplete(): boolean {
    if (this.prepRows.length === 0) return false;
    return this.prepRows.every((r) => this.prepRowPhase(r) === 'complete');
  }

  /**
   * Cold / early Setup: leaf selection + Start all only. Hide pipeline steps,
   * rivals, Finish setup, etc. until any prep domain has left idle (crawl
   * started or already complete from a prior run).
   */
  get isPrepScopingPhase(): boolean {
    if (!this.domain) return false;
    if (this.prepRows.length === 0) return true;
    return this.prepRows.every((r) => this.prepRowPhase(r) === 'idle');
  }

  get showTopicPrepAllPrompt(): boolean {
    return this.allPrepCrawlsComplete && this.hasPreppableCompetitors;
  }

  formatDurationMs(ms?: number): string | null {
    if (ms == null || !Number.isFinite(ms) || ms <= 0) return null;
    const sec = Math.round(ms / 1000);
    if (sec < 60) return `${sec}s`;
    const min = Math.floor(sec / 60);
    const rem = sec % 60;
    return rem > 0 ? `${min}m ${rem}s` : `${min}m`;
  }

  private startPolling(): void {
    if (this.pollSub) return;
    this.pollSub = interval(this.POLL_MS).subscribe(() => {
      // Pause polling while the Add-domain panel is open — re-rendering the
      // sitemap scoping UI mid-interaction would steal clicks/focus and reset
      // the live estimate.
      if (this.domain && !this.showAddDomain) this.loadAll(false);
    });
  }

  private stopPolling(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = null;
  }

  // --- Step model ---------------------------------------------------------

  private jobStateFor(type: ProcessingJobType): 'queued' | 'running' | null {
    const jobs = this.status?.activeJobs || [];
    const running = jobs.find((j) => j.type === type && j.status === 'running');
    if (running) return 'running';
    const queued = jobs.find((j) => j.type === type && j.status === 'queued');
    if (queued) return 'queued';
    return null;
  }

  /** Map a data-presence status + queue state into a unified step state. */
  private resolveState(
    dataStatus: string | undefined,
    jobType?: ProcessingJobType
  ): SetupStepState {
    if (jobType) {
      const queueState = this.jobStateFor(jobType);
      if (queueState) return queueState;
    }
    switch (dataStatus) {
      case 'complete':
        return 'complete';
      case 'partial':
        return 'partial';
      case 'blocked':
        return 'blocked';
      // `no_data` (zero analyzed pages) reads as "Not started" to users, so we
      // collapse the two — the only honest distinction (waiting on the crawl) is
      // handled by the crawl-completion gate in rebuildSteps().
      case 'no_data':
        return 'not_started';
      default:
        return 'not_started';
    }
  }

  /** True once the crawl has finished crawling AND analyzing all pages. */
  get analysisComplete(): boolean {
    const cs = this.crawlStatus;
    return Boolean(cs && (cs.isComplete || cs.phase === 'complete'));
  }

  private rebuildSteps(): void {
    const st = this.status;
    if (!st) {
      this.coreSteps = [];
      this.enrichmentSteps = [];
      this.optionalSteps = [];
      return;
    }

    const totalPages = st.linksBackfill?.totalPages ?? 0;
    const cs = this.crawlStatus;
    const phase = cs?.phase;
    const pagesCrawled = cs?.pagesCrawled ?? 0;
    const pagesAnalyzed = cs?.pagesAnalyzed ?? 0;
    // During the crawl phase totalUrls is the target (maxPages); during/after
    // analysis the backend reports totalUrls == pagesFound (the real crawled set).
    const crawlTotal = cs?.totalUrls ?? 0;
    const analysisComplete = this.analysisComplete;

    // Step 1 — Crawl: just the fetch. Considered done the moment we move on to
    // analysis, so the number never jumps backward (e.g. 715/715 -> 15/715).
    let crawlState: SetupStepState;
    if (!cs) crawlState = 'not_started';
    else if (cs.isComplete || phase === 'complete' || phase === 'analyzing') crawlState = 'complete';
    else if (phase === 'crawling') crawlState = 'running';
    else if (pagesCrawled > 0) crawlState = 'partial';
    else crawlState = 'not_started';

    // Step 2 — Analyze: our processing of crawled pages into PageAnalysis. This
    // is where the per-page work happens, so it owns the "X / Y pages" climb.
    const analyzeDenom = pagesCrawled || crawlTotal;
    let analyzeState: SetupStepState;
    if (!cs) analyzeState = 'not_started';
    else if (cs.isComplete || phase === 'complete') analyzeState = 'complete';
    else if (phase === 'analyzing') analyzeState = 'running';
    else analyzeState = 'not_started'; // still crawling — analysis hasn't begun

    // SS001-10. Analyze legitimately reads Complete here — it analyzed every page the
    // crawl actually returned — so the shortfall is against the *scoped* set and has to
    // be said out loud rather than folded into the state badge. Nothing is left to run,
    // which is why this is a warning and not a Partial.
    const analyzeShortfall = this.coverageShortfall;
    const analyzeWarning =
      analyzeShortfall?.severity === 'severe'
        ? `Only ${this.undercrawlCounts(analyzeShortfall)} — ${analyzeShortfall.missing.toLocaleString()} scoped URLs never became a page analysis. ` +
          'Everything below is built on the pages that did, so treat this domain as a sample of itself.'
        : undefined;

    // Everything downstream of analysis is gated until the crawl AND analysis are
    // done. While we wait we render "Waiting for crawl" instead of evaluating
    // partial/complete against an incomplete, growing page set (which caused the
    // confusing flicker and premature "Complete" badges). A job already in the
    // queue still shows its real running/queued state.
    const gate = (state: SetupStepState): SetupStepState => {
      if (analysisComplete) return state;
      if (state === 'queued' || state === 'running') return state;
      return 'waiting';
    };

    // Topics map is workspace-global, so existing mappings from other domains make
    // it look "Complete" the instant a new domain is crawled. staleForDomain (set
    // by the backend when the last map predates this domain's analysis) flags that
    // it must be re-run to incorporate this domain.
    let topicsMapState = gate(this.resolveState(st.topicsMap?.status, 'topics-map'));
    const topicsMapStale = Boolean(st.topicsMap?.staleForDomain);
    if (analysisComplete && topicsMapStale && topicsMapState !== 'queued' && topicsMapState !== 'running') {
      topicsMapState = 'not_started';
    }
    const topicsMapDetail = topicsMapStale
      ? `${st.topicsMap?.mappingCount ?? 0} mappings · re-run to include this domain`
      : `${st.topicsMap?.mappingCount ?? 0} mappings`;

    // Est. traffic: DataForSEO Labs only reports organic ETV for pages that
    // actually rank, so PARTIAL is expected (not a failure). Spell out the gap.
    const trafficTotal = st.estTraffic?.totalPages ?? totalPages;
    const trafficWith = st.estTraffic?.pagesWithTraffic ?? 0;
    const trafficState = gate(this.resolveState(st.estTraffic?.status));
    const trafficNote =
      analysisComplete && trafficState !== 'waiting' && trafficWith < trafficTotal
        ? trafficState === 'complete'
          ? `${trafficTotal - trafficWith} pages don't rank organically (ETV 0) — expected after Labs; not a failure.`
          : `${trafficTotal - trafficWith} pages have no DataForSEO organic-traffic data — they don't rank organically, so they stay blank. Partial is expected here, not a failure.`
        : undefined;

    const demandTotal = st.pageDemand?.totalPages ?? totalPages;
    const demandLabeled = st.pageDemand?.labeledUrls ?? 0;
    const demandState = gate(this.resolveState(st.pageDemand?.status));

    const serpTotal = st.urlSerp?.totalPages ?? totalPages;
    const serpWith = st.urlSerp?.urlsWithSnapshots ?? 0;
    const serpState = gate(this.resolveState(st.urlSerp?.status));
    const as = st.urlSerp?.answerStructure;
    const serpDetailParts = [`${serpWith} / ${serpTotal} pages with SERP snapshots`];
    if (as && as.uniqueKeywords > 0) {
      serpDetailParts.push(
        `answer-structure LLM ${as.cachedKeywords} / ${as.uniqueKeywords} unique queries` +
          ` (${as.urlsWithCachedStructure} / ${as.urlsWithSerp} pages)`
      );
    } else if (serpWith > 0) {
      serpDetailParts.push('answer-structure LLM not warmed yet — re-run this step');
    }
    const serpNote =
      analysisComplete && demandLabeled < demandTotal
        ? 'Run Page demand labels first for better SERP target-query resolution.'
        : analysisComplete && serpWith === 0 && demandLabeled > 0
          ? 'No SERP snapshots yet — run this step after Page demand labels (or re-run if Finish setup ran URL SERP too early).'
          : as && as.uniqueKeywords > 0 && as.cachedKeywords < as.uniqueKeywords
            ? `${as.uniqueKeywords - as.cachedKeywords} SERP queries still need answer-structure LLM — re-run URL SERP snapshots to warm.`
            : undefined;

    // Internal-link suggestions don't need topics-map/aggregate, but they DO use
    // classified link types + per-page topic signals + est. traffic for scoring.
    // If a build exists but classification isn't finished, the suggestions are
    // built against partial data — flag them as stale so the operator re-runs.
    const internalLinksState = gate(this.resolveState(st.internalLinkSuggestions?.status, 'action-plan-internal-links'));
    const classifyComplete = st.linksClassify?.status === 'complete';
    const internalLinksStale = internalLinksState === 'complete' && !classifyComplete;
    const internalLinksNote = internalLinksStale
      ? 'Built before link classification finished — re-run (via Finish setup) once Links classify completes for accurate scoring.'
      : undefined;

    // Supporting-pages builds cache the competitor set they were built against.
    // When the marked-competitor set changes, the backend reads return null and
    // the snapshot is stale — flag it as needs-run so the operator rebuilds it.
    const competitorStaleNote = 'Competitor set changed — re-run to include the current competitors.';
    const byTopicStale = analysisComplete && Boolean(st.supportingPagesByTopic?.staleForCompetitorSet);
    let supportingByTopicState = gate(this.resolveState(st.supportingPagesByTopic?.status, 'supporting-pages-by-topic'));
    if (byTopicStale && supportingByTopicState === 'complete') supportingByTopicState = 'not_started';
    const oppStale = analysisComplete && Boolean(st.supportingPagesOpportunities?.staleForCompetitorSet);
    let supportingOppState = gate(this.resolveState(st.supportingPagesOpportunities?.status, 'supporting-pages-opportunities'));
    if (oppStale && supportingOppState === 'complete') supportingOppState = 'not_started';

    this.coreSteps = [
      {
        key: 'crawl',
        title: 'Crawl',
        description: 'Fetch pages from the sitemap.',
        state: crawlState,
        detail: crawlTotal ? `${pagesCrawled} / ${crawlTotal} pages crawled` : `${pagesCrawled} pages crawled`,
        actionable: true,
        actionLabel: 'Open Crawl Management'
      },
      {
        key: 'analyze',
        title: 'Analyze',
        description: 'Process crawled pages into page analyses.',
        state: analyzeState,
        detail: analyzeDenom ? `${pagesAnalyzed} / ${analyzeDenom} pages analyzed` : `${pagesAnalyzed} pages analyzed`,
        warning: analyzeWarning,
        warningChip: analyzeWarning ? 'Undercrawled' : undefined,
        actionable: false,
        actionLabel: ''
      },
      {
        key: 'links-backfill',
        title: 'Links backfill',
        description: 'Fetch internal links from the crawl.',
        endpoint: 'links-backfill',
        jobType: 'links-backfill',
        state: gate(this.resolveState(st.linksBackfill?.status, 'links-backfill')),
        detail: `${st.linksBackfill?.pagesWithLinks ?? 0} / ${st.linksBackfill?.totalPages ?? 0} pages`,
        actionable: true,
        actionLabel: 'Run'
      },
      {
        key: 'links-classify',
        title: 'Links classify',
        description: 'Classify internal links (structural vs contextual).',
        endpoint: 'links-classify',
        jobType: 'links-classify',
        state: gate(this.resolveState(st.linksClassify?.status, 'links-classify')),
        detail: `${st.linksClassify?.pagesWithClassifiedLinks ?? 0} / ${st.linksClassify?.pagesWithLinks ?? 0} pages`,
        actionable: true,
        actionLabel: 'Run'
      },
      {
        key: 'topics-map',
        title: 'Topics map',
        description: 'Cluster topic signals into parent topics (workspace-global).',
        endpoint: 'topics-map',
        jobType: 'topics-map',
        state: topicsMapState,
        detail: topicsMapDetail,
        actionable: true,
        actionLabel: 'Run'
      },
      {
        key: 'topics-aggregate',
        title: 'Topics aggregate',
        description: 'Aggregate page topics into domain authority.',
        endpoint: 'topics-aggregate',
        jobType: 'topics-aggregate',
        state: gate(this.resolveState(st.topicsAggregate?.status, 'topics-aggregate')),
        detail: `${st.topicsAggregate?.aggregateCount ?? 0} topics`,
        actionable: true,
        actionLabel: 'Run'
      },
      {
        key: 'internal-links',
        title: 'Internal link suggestions',
        description: 'Build the internal-linking action plan.',
        endpoint: 'internal-link-suggestions',
        jobType: 'action-plan-internal-links',
        state: internalLinksState,
        detail: `${st.internalLinkSuggestions?.suggestionCount ?? 0} suggestions`,
        note: internalLinksNote,
        blockedReason: st.internalLinkSuggestions?.blockedReason,
        actionable: true,
        actionLabel: 'Run'
      },
      {
        key: 'supporting-by-topic',
        title: 'Supporting pages by topic',
        description: 'Topic-diagnostic supporting page snapshots.',
        endpoint: 'supporting-pages-by-topic',
        jobType: 'supporting-pages-by-topic',
        state: supportingByTopicState,
        detail: this.supportingDetail(st.supportingPagesByTopic),
        note: byTopicStale ? competitorStaleNote : undefined,
        blockedReason: st.supportingPagesByTopic?.blockedReason,
        actionable: true,
        actionLabel: 'Run'
      }
    ];

    this.enrichmentSteps = [
      {
        key: 'est-traffic',
        title: 'Estimated traffic',
        description: 'Organic traffic estimates (DataForSEO Labs). Auto-runs once after crawl.',
        endpoint: 'traffic-enrichment',
        state: trafficState,
        detail: `${trafficWith} / ${trafficTotal} pages with traffic data`,
        note: trafficNote,
        actionable: true,
        actionLabel: 'Refresh'
      },
      {
        key: 'est-keywords',
        title: 'Estimated keywords',
        description: 'Ranked keywords domain dump (DataForSEO Labs — incurs cost).',
        endpoint: 'ranked-keywords-enrichment',
        state: gate(this.resolveState(st.estKeywords?.status)),
        detail: `${st.estKeywords?.pagesWithKeywords ?? 0} / ${st.estKeywords?.totalPages ?? totalPages} pages`,
        actionable: true,
        actionLabel: 'Run (Labs cost)'
      },
      {
        key: 'page-demand',
        title: 'Page demand labels',
        description: 'Per-URL demand claims — Clusters scoring, Inventory health, Gap/Ratio prerequisites.',
        endpoint: 'page-demand-labeling',
        state: demandState,
        detail: `${demandLabeled} / ${demandTotal} pages labeled`,
        actionable: true,
        actionLabel: 'Run'
      },
      {
        key: 'url-serp',
        title: 'URL SERP snapshots',
        description:
          'Live SERP top-10 per tracked URL (Labs cost; cache-first unless Force API). Also warms answer-structure LLM cache so URL Detail expand is free.',
        state: serpState,
        detail: serpDetailParts.join(' · '),
        note: serpNote,
        actionable: true,
        actionLabel: 'Run (Labs cost)'
      }
    ];

    this.optionalSteps = [
      {
        key: 'supporting-opportunities',
        title: 'Supporting page opportunities',
        description: 'Content-gap opportunities (requires the content-gap sidecar; slow).',
        endpoint: 'supporting-pages-opportunities',
        jobType: 'supporting-pages-opportunities',
        state: supportingOppState,
        detail: this.supportingDetail(st.supportingPagesOpportunities),
        note: oppStale ? competitorStaleNote : undefined,
        blockedReason: st.supportingPagesOpportunities?.blockedReason,
        optional: true,
        actionable: true,
        actionLabel: 'Run'
      }
    ];
  }

  private supportingDetail(s?: { snapshotCount: number; processedUrls: number; totalUrls: number; progressPercent: number }): string {
    if (!s) return '';
    if (s.totalUrls > 0 && s.processedUrls < s.totalUrls) {
      return `${s.processedUrls} / ${s.totalUrls} URLs (${s.progressPercent}%)`;
    }
    return `${s.snapshotCount} snapshots`;
  }

  // --- Progress -----------------------------------------------------------

  get completeCount(): number {
    return this.allSteps.filter((s) => s.state === 'complete').length;
  }

  get totalCount(): number {
    return this.allSteps.length;
  }

  private get allSteps(): SetupStep[] {
    return [...this.coreSteps, ...this.enrichmentSteps, ...this.optionalSteps];
  }

  /** Minimum pipeline + enrichment needed before Inventory issues / Action Plan are trustworthy. */
  get reportReady(): boolean {
    if (!this.analysisComplete) return false;
    const requiredKeys = [
      'links-backfill',
      'links-classify',
      'topics-map',
      'topics-aggregate',
      'internal-links',
      'page-demand'
    ];
    return requiredKeys.every((key) => {
      const step = this.allSteps.find((s) => s.key === key);
      return step?.state === 'complete' || step?.state === 'partial';
    });
  }

  get reportReadyBlockers(): string[] {
    const blockers: string[] = [];
    if (!this.analysisComplete) blockers.push('Crawl and analyze must finish.');
    const check = (key: string, label: string) => {
      const step = this.allSteps.find((s) => s.key === key);
      if (!step) return;
      if (step.state !== 'complete' && step.state !== 'partial') {
        blockers.push(`${label} — ${this.stateLabel(step.state).toLowerCase()}`);
      }
    };
    check('links-backfill', 'Links backfill');
    check('links-classify', 'Links classify');
    check('topics-map', 'Topics map');
    check('topics-aggregate', 'Topics aggregate');
    check('internal-links', 'Internal link suggestions');
    check('page-demand', 'Page demand labels');
    return blockers;
  }

  get enrichmentGaps(): string[] {
    const gaps: string[] = [];
    const serp = this.enrichmentSteps.find((s) => s.key === 'url-serp');
    if (serp && serp.state !== 'complete' && serp.state !== 'partial') {
      gaps.push('URL SERP — needed for Gap / Ratio / KD columns in Inventory');
    }
    const kw = this.enrichmentSteps.find((s) => s.key === 'est-keywords');
    if (kw && kw.state !== 'complete' && kw.state !== 'partial') {
      gaps.push('Estimated keywords — optional unless you need ranked-keywords data');
    }
    return gaps;
  }

  get progressPercent(): number {
    if (this.totalCount === 0) return 0;
    return Math.round((this.completeCount / this.totalCount) * 100);
  }

  // --- "You are here" (SS001-9) -------------------------------------------

  /** Crawl phase of a prep row, collapsed to what the guide cares about. */
  private prepRowPhase(row: PrepDomainRow): 'idle' | 'crawling' | 'analyzing' | 'complete' {
    if (row.crawlStarting) return 'crawling';
    const cs = this.crawlStatusFor(row.domain);
    if (!cs) return 'idle';
    if (cs.isComplete || cs.phase === 'complete') return 'complete';
    if (cs.phase === 'analyzing') return 'analyzing';
    if (cs.phase === 'crawling') return 'crawling';
    return 'idle';
  }

  /**
   * The one banner that answers "where am I, do I have to do anything, how long".
   * `x / 13 steps complete` alone is what SS001-9 called out as unreadable.
   */
  get setupGuide(): SetupGuide {
    const liveDomains = this.prepRows
      .filter((r) => {
        const phase = this.prepRowPhase(r);
        return phase === 'crawling' || phase === 'analyzing';
      })
      .map((r) => r.domain);

    return deriveSetupGuide({
      hasDomain: Boolean(this.domain),
      prep: this.prepRows.map((r) => ({
        domain: r.domain,
        role: r.role,
        hasSitemapEstimate: Boolean(r.estimate),
        phase: this.prepRowPhase(r)
      })),
      competitors: this.competitors.map((c) => ({
        domain: c.domain,
        crawled: c.crawled,
        hasTopicSignals: c.hasTopicSignals,
        ready: c.ready,
        activeJob: c.activeJob,
        contentBlocked: this.competitorContentBlocked(c)
      })),
      pipelineBusy: this.pipelineBusy,
      reportReady: this.reportReady,
      eta: this.etaTracker.aggregate(liveDomains)
    });
  }

  // --- Finish setup / Run all --------------------------------------------

  get crawlReady(): boolean {
    return Boolean(this.crawlStatus?.isComplete) && (this.crawlStatus?.pagesAnalyzed ?? this.status?.linksBackfill?.totalPages ?? 0) > 0;
  }

  /** True while any pipeline step is queued/running or a request is in flight. */
  get pipelineBusy(): boolean {
    if (this.runningAll) return true;
    for (const v of this.busyByKey.values()) if (v) return true;
    return [...this.coreSteps, ...this.enrichmentSteps, ...this.optionalSteps].some(
      (s) => s.state === 'queued' || s.state === 'running'
    );
  }

  get finishSetupBlockedReason(): string | null {
    if (!this.crawlStatus) return 'No crawl yet — start a crawl first.';
    if (!this.crawlStatus.isComplete) return 'Crawl still in progress — wait for it to finish.';
    const pages = this.crawlStatus.pagesAnalyzed ?? this.status?.linksBackfill?.totalPages ?? 0;
    if (pages <= 0) return 'Crawl produced 0 analyzed pages — nothing to process.';
    if (this.pipelineBusy) return 'Processing is already running — wait for the current steps to finish.';

    const comps = this.competitors;
    if (comps.length > 0) {
      for (const c of comps) {
        if (!c.crawled) {
          if (this.competitorCrawlInFlight(c)) {
            return `Waiting for ${c.domain} crawl — ${setupCrawlChip(this.crawlPhaseFor(c.domain)).label.toLowerCase()} now.`;
          }
          return `Waiting for ${c.domain} crawl — use Prep all domains or add competitor.`;
        }
        if (!c.hasTopicSignals && !this.competitorContentBlocked(c)) {
          return `Waiting for ${c.domain} analysis to finish.`;
        }
        if (!c.ready) {
          return `Run topic prep for all — ${c.domain} is not ready yet.`;
        }
      }
    }
    return null;
  }

  private clearPendingFinishOptIns(): void {
    this.pendingFinishOptIns = null;
    this.finishOptInGapWarned = false;
    this.clearPersistedPaidOptInsIntent();
  }

  /**
   * Keep the free page-demand pass automatic now that the core pipeline can be
   * enqueued by the backend (SS001-11) instead of by this component. It is $0
   * CPU and a prerequisite for Issues, so waiting for a click would be exactly
   * the babysitting A2 removes — but it is not a queue job type, so only the
   * frontend can start it.
   *
   * Fires once the pipeline has actually started (mirroring the old "right
   * after Finish setup" timing) and only when demand labels have never been
   * built for this domain.
   */
  private maybeAutoRunPageDemand(): void {
    if (!this.domain || this.pageDemandAutoAttemptedFor === this.domain) return;
    if (!this.analysisComplete || this.isBusy('page-demand')) return;

    const demand = this.allSteps.find((s) => s.key === 'page-demand');
    const backfill = this.allSteps.find((s) => s.key === 'links-backfill');
    if (!demand || !backfill) return;
    // "The pipeline is under way" — whoever started it.
    if (!['queued', 'running', 'partial', 'complete'].includes(backfill.state)) return;
    if (demand.state !== 'not_started' && demand.state !== 'no_data') return;

    this.pageDemandAutoAttemptedFor = this.domain;
    this.runStepByKey('page-demand', true);
  }

  /**
   * Finish-setup opt-ins must wait for prerequisites:
   * - page demand before URL SERP / est. keywords
   * - supporting-pages-by-topic (+ content-gap sidecar) before opportunities
   */
  private tryRunPendingFinishOptIns(): void {
    const pending = this.pendingFinishOptIns;
    if (!pending || !this.domain || !this.status) return;
    // Opt-ins may be queued at Start all — wait until the focal crawl is done.
    if (!this.crawlReady) return;

    const st = this.status;
    const demandLabeled = st.pageDemand?.labeledUrls ?? 0;
    const demandTotal = st.pageDemand?.totalPages ?? 0;
    const demandReady =
      st.pageDemand?.status === 'complete'
      || st.pageDemand?.status === 'partial'
      || (demandTotal > 0 && demandLabeled >= demandTotal);

    if (pending.pageDemand) {
      if (demandReady) {
        pending.pageDemand = false;
      } else if (this.isBusy('page-demand')) {
        return;
      } else {
        this.runStepByKey('page-demand', true);
        return;
      }
    }

    if (!demandReady && (pending.urlSerp || pending.keywords)) {
      return;
    }

    if (pending.keywords && !this.isBusy('est-keywords')) {
      const kw = st.estKeywords?.status;
      if (kw === 'complete' || kw === 'partial') {
        pending.keywords = false;
      } else {
        pending.keywords = false;
        this.runStepByKey('est-keywords', true);
        return;
      }
    }

    if (pending.urlSerp && !this.isBusy('url-serp')) {
      const serpWith = st.urlSerp?.urlsWithSnapshots ?? 0;
      const serpStatus = st.urlSerp?.status;
      if (serpWith > 0 || serpStatus === 'complete' || serpStatus === 'partial') {
        pending.urlSerp = false;
      } else {
        pending.urlSerp = false;
        this.runUrlSerpStep(true);
        return;
      }
    }

    if (pending.topSerpRivals) {
      // Wait until URL SERP is no longer running so the rival set is as complete
      // as this run will get (partial with 0 busy = done as it will be).
      if (pending.urlSerp || this.isBusy('url-serp')) return;
      if (this.topSerpRivalsRunning || this.topSerpRivalsBuilding()) return;
      if (this.topSerpRivalsStatus?.status === 'ready') {
        pending.topSerpRivals = false;
      } else {
        const serpWith = st.urlSerp?.urlsWithSnapshots ?? 0;
        const serpStatus = st.urlSerp?.status;
        if (serpWith === 0 && serpStatus !== 'complete' && serpStatus !== 'partial') {
          return;
        }
        if (this.topSerpRivalsLoading) return;
        if (!this.topSerpRivals) {
          this.refreshTopSerpRivalsPreview();
          return;
        }
        if (this.topSerpRivals.candidateFocals <= 0) {
          pending.topSerpRivals = false;
          this.notifications.info(
            'Top SERP rivals skipped — no outranking rivals on current SERP snapshots.'
          );
        } else {
          pending.topSerpRivals = false;
          this.startTopSerpRivalsChain();
          return;
        }
      }
    }

    if (pending.opportunities && !this.isBusy('supporting-opportunities')) {
      const byTopic = st.supportingPagesByTopic;
      const byTopicBusy = this.jobStateFor('supporting-pages-by-topic');
      if (byTopicBusy || byTopic?.status === 'partial') {
        return;
      }
      if (byTopic?.status !== 'complete') {
        return;
      }
      if (!st.supportingPagesOpportunities?.contentGapAvailable) {
        if (!this.finishOptInGapWarned) {
          this.finishOptInGapWarned = true;
          this.notifications.info(
            'Supporting page opportunities skipped — start the content-gap sidecar (npm run content-gap:serve), then click Run on that step.'
          );
        }
        pending.opportunities = false;
      } else if (this.jobStateFor('supporting-pages-opportunities') == null) {
        pending.opportunities = false;
        this.runStepByKey('supporting-opportunities', true);
        return;
      }
    }

    if (
      !pending.keywords
      && !pending.urlSerp
      && !pending.topSerpRivals
      && !pending.opportunities
      && !pending.pageDemand
    ) {
      this.clearPendingFinishOptIns();
    }
  }

  /**
   * Merge follow-on steps into the pending chain instead of replacing it, so a
   * Finish setup click cannot silently drop a paid run the operator already
   * confirmed (or the other way round).
   */
  private queuePendingOptIns(next: {
    pageDemand?: boolean;
    keywords?: boolean;
    urlSerp?: boolean;
    topSerpRivals?: boolean;
    opportunities?: boolean;
  }): void {
    const current = this.pendingFinishOptIns || {
      pageDemand: false,
      keywords: false,
      urlSerp: false,
      topSerpRivals: false,
      opportunities: false
    };
    this.pendingFinishOptIns = {
      pageDemand: current.pageDemand || Boolean(next.pageDemand),
      keywords: current.keywords || Boolean(next.keywords),
      urlSerp: current.urlSerp || Boolean(next.urlSerp),
      topSerpRivals: current.topSerpRivals || Boolean(next.topSerpRivals),
      opportunities: current.opportunities || Boolean(next.opportunities)
    };
  }

  /** The checked paid / sidecar steps, named the way the confirm should name them. */
  get selectedPaidEnrichments(): string[] {
    const picked: string[] = [];
    if (this.includeKeywords) picked.push('Estimated keywords (DataForSEO Labs — costs money)');
    if (this.includeUrlSerp || this.includeTopSerpRivals) {
      picked.push('URL SERP snapshots (DataForSEO Labs — costs money)');
    }
    if (this.includeTopSerpRivals) {
      picked.push(
        'Top SERP rivals — crawl best rival pages + fetch their ranked keywords (crawl + Labs cost; exact $ after URL SERP)'
      );
    }
    if (this.includeOpportunities) picked.push('Supporting page opportunities (needs the content-gap sidecar; slow)');
    return picked;
  }

  get paidEnrichmentsBlockedReason(): string | null {
    if (this.selectedPaidEnrichments.length === 0) return 'Check at least one paid step above.';
    if (!this.crawlReady) return 'Crawl must finish first.';
    return null;
  }

  /**
   * @deprecated Prefer prep-screen opt-in + Start all confirm. Kept so a mid-run
   * re-confirm path can reuse the same queue if we wire a button later.
   */
  runPaidEnrichments(): void {
    if (!this.domain || this.runningPaidEnrichments) return;
    const blocked = this.paidEnrichmentsBlockedReason;
    if (blocked) {
      this.notifications.error(blocked);
      return;
    }
    const steps = this.selectedPaidEnrichments;
    const confirmed = window.confirm(
      `Run these now?\n\n${steps.map((s) => `• ${s}`).join('\n')}\n\n`
        + 'They run against every tracked URL for this domain and are billed by DataForSEO. '
        + 'Nothing starts until you confirm.'
    );
    if (!confirmed) return;

    this.runningPaidEnrichments = true;
    this.finishOptInGapWarned = false;
    this.queuePendingOptIns({
      pageDemand: this.includeKeywords || this.includeUrlSerp || this.includeTopSerpRivals,
      keywords: this.includeKeywords,
      urlSerp: this.includeUrlSerp || this.includeTopSerpRivals,
      topSerpRivals: this.includeTopSerpRivals,
      opportunities: this.includeOpportunities
    });
    this.persistPaidOptInsIntent();
    this.notifications.success('Paid enrichments queued — they start as their prerequisites finish.');
    this.tryRunPendingFinishOptIns();
    this.runningPaidEnrichments = false;
  }

  /**
   * Manual re-run of the CORE pipeline. Since SS001-11 the backend enqueues this
   * same batch on its own once the prep set is ready; the button stays as the
   * escape hatch (gate disabled, a step failed, artifacts need rebuilding) and
   * runs exactly what the automatic path runs — no paid steps.
   */
  finishSetup(): void {
    if (!this.domain || this.runningAll) return;
    if (this.finishSetupBlockedReason) {
      this.notifications.error(this.finishSetupBlockedReason);
      return;
    }
    this.runningAll = true;
    const scopedDomains =
      this.isDevView() && this.topicsMapScope === 'selected'
        ? Array.from(this.topicsMapDomains)
        : undefined;
    // Free follow-on only ($0 CPU, SETUP.md step taxonomy). Paid steps were
    // confirmed at Start all (prep-screen opt-ins).
    this.queuePendingOptIns({ pageDemand: true });
    this.crawl.runAllProcessing(this.domain, scopedDomains).subscribe({
      next: (res) => {
        this.notifications.success(res?.message || 'All processing steps queued.');
        this.runningAll = false;
        this.loadAll(false);
      },
      error: (err) => {
        this.notifications.error(err?.error?.message || err?.message || 'Failed to queue processing.');
        this.runningAll = false;
      }
    });
  }

  // --- Per-step run -------------------------------------------------------

  isBusy(key: string): boolean {
    return this.busyByKey.get(key) === true;
  }

  private stepByKey(key: string): SetupStep | undefined {
    return this.allSteps.find((s) => s.key === key);
  }

  /** True if any (transitive) upstream dependency is queued/running/busy. */
  private hasUpstreamInFlight(key: string, seen = new Set<string>()): boolean {
    for (const dep of this.stepDeps[key] || []) {
      if (seen.has(dep)) continue;
      seen.add(dep);
      if (this.isBusy(dep)) return true;
      const s = this.stepByKey(dep);
      if (s && (s.state === 'queued' || s.state === 'running')) return true;
      if (this.hasUpstreamInFlight(dep, seen)) return true;
    }
    return false;
  }

  /** Whether a step's Run/Refresh action should be disabled. */
  isStepActionDisabled(step: SetupStep): boolean {
    return this.stepActionDisabledReason(step) !== null;
  }

  /** Human-readable reason a step's action is disabled (also used as tooltip). */
  stepActionDisabledReason(step: SetupStep): string | null {
    // Crawl's action is pure navigation — always available.
    if (step.key === 'crawl') return null;
    if (this.isBusy(step.key)) return 'Starting…';
    if (step.state === 'queued') return 'Already queued.';
    if (step.state === 'running') return 'Already running.';
    if (step.state === 'waiting') return 'Waiting for the crawl to finish.';
    // Blocked unless the sidecar was the issue (start sidecar + Refresh, then Run here).
    if (step.state === 'blocked' && !this.isSidecarBlocked(step)) {
      return step.blockedReason || 'Blocked — resolve the prerequisite first.';
    }
    if (this.hasUpstreamInFlight(step.key)) {
      return 'A prerequisite step is still queued/running — wait for it to finish.';
    }
    return null;
  }

  runStep(step: SetupStep): void {
    if (step.key === 'crawl') {
      this.router.navigate(['/crawl-management']);
      return;
    }
    if (step.key === 'url-serp') {
      if (this.isStepActionDisabled(step)) return;
      this.runUrlSerpStep();
      return;
    }
    if (this.isStepActionDisabled(step)) return;
    this.runStepByKey(step.key);
  }

  private runUrlSerpStep(silent = false): void {
    if (!this.domain || this.isBusy('url-serp')) return;
    this.busyByKey.set('url-serp', true);
    this.urlSerpProgress = null;
    void this.urlSerpSync
      .refreshDomainBatched(this.domain, {
        forceRefresh: this.urlSerpForceRefresh,
        onProgress: (progress) => {
          this.urlSerpProgress = progress;
        }
      })
      .then((totals) => {
        if (!silent) {
          this.notifications.success(
            `URL SERP refresh complete — ${totals.snapshotsWritten} snapshots, ~$${totals.estimatedCostUsd.toFixed(2)}`
          );
        }
        this.busyByKey.set('url-serp', false);
        this.urlSerpProgress = null;
        this.loadAll(false);
      })
      .catch((err) => {
        if (!silent) {
          this.notifications.error(err?.message || 'URL SERP refresh failed.');
        }
        this.busyByKey.set('url-serp', false);
        this.urlSerpProgress = null;
      });
  }

  private runStepByKey(key: string, silent = false): void {
    const step = this.allSteps.find((s) => s.key === key);
    if (!step || !step.endpoint || !this.domain) return;
    if (this.isBusy(key)) return;
    this.busyByKey.set(key, true);
    this.crawl.runProcessEndpoint(this.domain, step.endpoint).subscribe({
      next: (res) => {
        if (!silent) this.notifications.success(res?.message || `${step.title} started.`);
        this.busyByKey.set(key, false);
        this.loadAll(false);
      },
      error: (err) => {
        if (!silent) this.notifications.error(err?.error?.message || err?.message || `Failed to start ${step.title}.`);
        this.busyByKey.set(key, false);
      }
    });
  }

  // --- Competitors --------------------------------------------------------

  get competitors(): CompetitorReadiness[] {
    return this.competitorStatus?.competitors || [];
  }

  /** Any competitor that is crawled + has topic signals but isn't ready/running. */
  get hasPreppableCompetitors(): boolean {
    return this.competitors.some((c) => this.canPrepCompetitor(c));
  }

  /**
   * Same crawl truth as the Prep list, then topic-prep readiness on top — so a
   * competitor mid-crawl can never read "Not crawled" here while the Prep list
   * says "Crawling" (SS001-8).
   */
  competitorChip(c: CompetitorReadiness): SetupStatusChip {
    return setupCompetitorChip({
      crawlPhase: this.crawlPhaseFor(c.domain),
      activeJob: c.activeJob,
      ready: c.ready,
      hasTopicSignals: c.hasTopicSignals,
      pagesAnalyzed: c.pagesAnalyzed,
      pagesWithContent: c.pagesWithContent
    });
  }

  /** True while a competitor's own crawl is still running (chip + notes). */
  competitorCrawlInFlight(c: CompetitorReadiness): boolean {
    const phase = this.crawlPhaseFor(c.domain);
    return phase === 'crawling' || phase === 'analyzing' || phase === 'starting';
  }

  /**
   * Pages were analyzed but none captured extractable text — the crawler got a
   * head-only/error shell (client-rendered or bot-blocked site), so no topic
   * signals can be extracted. Distinct from "still analyzing".
   */
  competitorContentBlocked(c: CompetitorReadiness): boolean {
    return c.crawled && !c.hasTopicSignals && c.pagesAnalyzed > 0 && (c.pagesWithContent ?? 0) === 0;
  }

  canPrepCompetitor(c: CompetitorReadiness): boolean {
    return c.crawled && c.hasTopicSignals && !c.ready && c.activeJob === null;
  }

  isCompetitorBusy(domain: string): boolean {
    return this.competitorBusy.get(domain) === true;
  }

  runCompetitorPrep(c: CompetitorReadiness): void {
    if (!this.canPrepCompetitor(c) || this.isCompetitorBusy(c.domain)) return;
    this.competitorBusy.set(c.domain, true);
    this.crawl.runCompetitorPrep(c.domain, this.domain).subscribe({
      next: (res) => {
        this.notifications.success(res?.message || `Topic prep queued for ${c.domain}.`);
        this.competitorBusy.set(c.domain, false);
        this.loadAll(false);
      },
      error: (err) => {
        this.notifications.error(err?.error?.message || err?.message || 'Failed to queue topic prep.');
        this.competitorBusy.set(c.domain, false);
      }
    });
  }

  runCompetitorPrepAll(): void {
    if (!this.domain || this.runningCompetitorPrepAll) return;
    this.runningCompetitorPrepAll = true;
    this.crawl.runCompetitorPrepAll(this.domain).subscribe({
      next: (res) => {
        this.notifications.success(res?.message || 'Topic prep queued for all competitors.');
        this.runningCompetitorPrepAll = false;
        this.loadAll(false);
      },
      error: (err) => {
        this.notifications.error(err?.error?.message || err?.message || 'Failed to queue topic prep.');
        this.runningCompetitorPrepAll = false;
      }
    });
  }

  removeCompetitor(c: CompetitorReadiness): void {
    if (!this.domain || this.isCompetitorBusy(c.domain)) return;
    this.competitorBusy.set(c.domain, true);
    this.compareContext.removeMarkedCompetitor(c.domain, this.domain).subscribe({
      next: () => {
        this.notifications.success(`Removed ${c.domain} as a competitor.`);
        this.competitorBusy.set(c.domain, false);
        this.loadAll(false);
      },
      error: (err) => {
        this.notifications.error(err?.error?.message || err?.message || 'Failed to remove competitor.');
        this.competitorBusy.set(c.domain, false);
      }
    });
  }

  /** True when a SERP-suggested domain is already a marked competitor of the focal domain. */
  isSuggestionAlreadyCompetitor(domain: string): boolean {
    const norm = this.normalizeDomain(domain);
    return (this.competitorStatus?.competitors || []).some((c) => this.normalizeDomain(c.domain) === norm);
  }

  /**
   * Open the Add-competitor panel pre-filled with a SERP-suggested domain and its
   * ranking pages as a synthesized sitemap (upload path). The operator reviews the
   * URL list + estimate, then clicks Start to crawl only those pages and mark the
   * domain as a competitor.
   */
  addSerpSuggestionAsCompetitor(suggestion: SerpCompetitorSuggestion): void {
    if (!this.domain) return;
    const domain = this.normalizeDomain(suggestion.domain);
    const pageUrls = (suggestion.pageUrls || []).map((u) => (u || '').trim()).filter(Boolean);
    if (!domain || pageUrls.length === 0) {
      this.notifications.error('No ranking pages available for this suggestion.');
      return;
    }

    this.addMode = 'competitor';
    this.showAddDomain = true;
    this.resetAddDomainForm();
    this.newDomainInput = domain;

    const xml = this.buildUrlsetXml(pageUrls);
    const file = new File([xml], `${domain}-serp-pages.xml`, { type: 'application/xml' });
    this.uploadedSitemapFiles = [file];
    this.usingUploadedSitemaps = true;
    this.estimateFromUploadedSitemaps(false);

    setTimeout(() => {
      document.getElementById('setup-add-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  }

  /** Build a standard urlset sitemap XML document from absolute URLs (order preserved). */
  private buildUrlsetXml(urls: string[]): string {
    const escapeXml = (value: string): string =>
      value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
    const entries = urls
      .filter((url) => typeof url === 'string' && url.trim().length > 0)
      .map((url) => `  <url><loc>${escapeXml(url.trim())}</loc></url>`)
      .join('\n');
    return [
      '<?xml version="1.0" encoding="UTF-8"?>',
      '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">',
      entries,
      '</urlset>'
    ].join('\n');
  }

  markExistingCompetitor(): void {
    const competitor = this.normalizeDomain(this.markCompetitorInput);
    if (!competitor || !this.domain) {
      this.notifications.error('Enter a domain to mark as a competitor.');
      return;
    }
    if (competitor === this.normalizeDomain(this.domain)) {
      this.notifications.error('A domain cannot be its own competitor.');
      return;
    }
    this.compareContext.addMarkedCompetitor(competitor, this.domain).subscribe({
      next: () => {
        this.notifications.success(`Marked ${competitor} as a competitor.`);
        this.markCompetitorInput = '';
        this.loadAll(false);
      },
      error: (err) => {
        this.notifications.error(err?.error?.message || err?.message || 'Failed to mark competitor.');
      }
    });
  }

  // --- Add domain ---------------------------------------------------------

  openAddDomain(mode: 'focal' | 'competitor'): void {
    this.addMode = mode;
    this.showAddDomain = true;
    this.resetAddDomainForm();
    // The Add panel renders at the top of the page, but this is triggered from
    // the Competitors card at the bottom — scroll it into view so it doesn't
    // look like nothing happened.
    setTimeout(() => {
      document.getElementById('setup-add-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  }

  toggleAddDomain(): void {
    this.showAddDomain = !this.showAddDomain;
    if (this.showAddDomain) this.addMode = 'focal';
    this.resetAddDomainForm();
  }

  private resetAddDomainForm(): void {
    this.estimate = null;
    this.checkedDomain = null;
    this.estimateError = null;
    this.botProtected = false;
    this.uploadedSitemapFiles = [];
    this.usingUploadedSitemaps = false;
    this.bypassCrawlCap = false;
    this.localePartitionedSitemaps = false;
    this.availableLeafSitemaps = [];
    this.selectedSitemaps.clear();
    this.availableLocaleGroups = [];
    this.selectedLocales.clear();
    this.neverTrackChoices.clear();
    this.perSitemapLimits = {};
    this.minLastmodFrom = '';
  }

  private normalizeDomain(value: string): string {
    return value
      .toLowerCase()
      .trim()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .split('/')[0];
  }

  estimateNewDomain(preserveSelection = false): void {
    const domain = this.normalizeDomain(this.newDomainInput);
    if (!domain) {
      this.estimateError = 'Enter a domain (e.g. example.com).';
      return;
    }
    if (this.usingUploadedSitemaps && this.uploadedSitemapFiles.length > 0) {
      this.estimateFromUploadedSitemaps(preserveSelection);
      return;
    }
    this.estimateLoading = true;
    this.estimateError = null;
    this.botProtected = false;
    if (!preserveSelection) {
      this.perSitemapLimits = {};
    }
    this.crawl
      .estimateCrawl(domain, this.buildEstimateOptions(preserveSelection))
      .subscribe({
        next: (res) => this.applyEstimateResponse(res.data, preserveSelection, domain),
        error: (err) => this.handleEstimateError(err, domain)
      });
  }

  onSitemapFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []).filter((file) =>
      /\.(xml|gz)$/i.test(file.name)
    );
    input.value = '';
    if (files.length === 0) {
      this.notifications.error('Choose one or more .xml sitemap files.');
      return;
    }
    const domain = this.normalizeDomain(this.newDomainInput);
    if (!domain) {
      this.estimateError = 'Enter a domain first, then upload sitemap files.';
      return;
    }
    this.uploadedSitemapFiles = files;
    this.usingUploadedSitemaps = true;
    this.botProtected = false;
    this.estimateError = null;
    this.estimateFromUploadedSitemaps(false);
  }

  clearUploadedSitemaps(): void {
    this.uploadedSitemapFiles = [];
    this.usingUploadedSitemaps = false;
    this.estimate = null;
    this.checkedDomain = null;
    this.availableLeafSitemaps = [];
    this.selectedSitemaps.clear();
    this.availableLocaleGroups = [];
    this.selectedLocales.clear();
    this.neverTrackChoices.clear();
    this.perSitemapLimits = {};
  }

  private estimateFromUploadedSitemaps(preserveSelection = false): void {
    const domain = this.normalizeDomain(this.newDomainInput);
    if (!domain || this.uploadedSitemapFiles.length === 0) return;
    this.estimateLoading = true;
    this.estimateError = null;
    if (!preserveSelection) {
      this.perSitemapLimits = {};
    }
    this.crawl
      .estimateCrawlFromUpload(domain, this.uploadedSitemapFiles, this.buildEstimateOptions(preserveSelection))
      .subscribe({
        next: (res) => this.applyEstimateResponse(res.data, preserveSelection, domain),
        error: (err) => this.handleEstimateError(err, domain)
      });
  }

  private buildEstimateOptions(preserveSelection: boolean) {
    return {
      limit: this.getEffectiveCrawlLimit(),
      selectedSitemaps: preserveSelection ? Array.from(this.selectedSitemaps) : [],
      selectedLocales: preserveSelection ? Array.from(this.selectedLocales) : [],
      minLastmodFrom: this.minLastmodFrom || null,
      perSitemapLimits: this.perSitemapLimits,
      perSitemapLimitMode: this.perSitemapLimitMode,
      keepNeverTrackKeys: preserveSelection ? this.keepNeverTrackKeys : [],
      excludeNeverTrackKeys: preserveSelection ? this.excludeNeverTrackKeys : []
    };
  }

  isDevView(): boolean {
    return !this.featureFlags.isProduction() && !this.featureFlags.isUserView();
  }

  /** Candidate domains for scoped topics:map: the focal domain + its competitors. */
  get topicsMapCandidates(): string[] {
    const candidates = [this.domain, ...this.competitors.map((c) => c.domain)]
      .map((d) => (d || '').trim().toLowerCase())
      .filter(Boolean);
    return [...new Set(candidates)];
  }

  onTopicsMapScopeChange(scope: 'all' | 'selected'): void {
    this.topicsMapScope = scope;
    // Prefill with all candidates when switching to "selected" so the common
    // case (focal + competitors) is one click.
    if (scope === 'selected') {
      this.topicsMapDomains = new Set(this.topicsMapCandidates);
    }
  }

  isTopicsMapDomainSelected(domain: string): boolean {
    return this.topicsMapDomains.has(domain);
  }

  toggleTopicsMapDomain(domain: string): void {
    if (this.topicsMapDomains.has(domain)) {
      this.topicsMapDomains.delete(domain);
    } else {
      this.topicsMapDomains.add(domain);
    }
  }

  private getEffectiveCrawlLimit(): number {
    return this.isDevView() && this.bypassCrawlCap ? this.devCrawlLimit : this.defaultCrawlLimit;
  }

  onBypassCapChange(): void {
    if (this.estimate) {
      this.refreshEstimateForFilters();
    }
  }

  private fetchCrawlUrlData(): Promise<{ urlsToCrawl: string[]; preview: CrawlUrlPreview }> {
    const domain = this.normalizeDomain(this.newDomainInput);
    if (!domain) {
      return Promise.reject(new Error('Enter a domain first'));
    }
    this.previewUrlsLoading = true;
    const options = { ...this.buildEstimateOptions(true), includeUrls: true };
    const req = this.usingUploadedSitemaps
      ? this.crawl.estimateCrawlFromUpload(domain, this.uploadedSitemapFiles, options)
      : this.crawl.estimateCrawl(domain, options);

    return new Promise((resolve, reject) => {
      req.subscribe({
        next: (res) => {
          this.previewUrlsLoading = false;
          const urls = res.data?.urlsToCrawl || [];
          const preview = res.data?.crawlUrlPreview || { included: [], excluded: [] };
          if (urls.length === 0 && preview.included.length === 0) {
            reject(new Error('No URLs matched the current filters'));
            return;
          }
          resolve({ urlsToCrawl: urls, preview });
        },
        error: (err) => {
          this.previewUrlsLoading = false;
          reject(err);
        }
      });
    });
  }

  private sortPreviewEntries(entries: CrawlUrlPreviewEntry[]): CrawlUrlPreviewEntry[] {
    return [...entries].sort((a, b) => {
      const sm = (a.sourceSitemapUrl || '').localeCompare(b.sourceSitemapUrl || '');
      if (sm !== 0) return sm;
      if (b.priority !== a.priority) return b.priority - a.priority;
      return a.url.localeCompare(b.url);
    });
  }

  private readonly sitemapPreviewColors = [
    '#e3f2fd', '#f3e5f5', '#e8f5e9', '#fff3e0', '#fce4ec', '#e0f7fa', '#f1f8e9', '#ede7f6'
  ];

  private buildSitemapColorMap(entries: CrawlUrlPreviewEntry[]): Map<string, { bg: string; fg: string }> {
    const sitemaps = [...new Set(entries.map((e) => e.sourceSitemapUrl || '').filter(Boolean))].sort();
    const map = new Map<string, { bg: string; fg: string }>();
    sitemaps.forEach((sm, i) => {
      map.set(sm, { bg: this.sitemapPreviewColors[i % this.sitemapPreviewColors.length], fg: '#1a1a1a' });
    });
    return map;
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private buildCrawlUrlPreviewHtml(domain: string, preview: CrawlUrlPreview): string {
    const included = this.sortPreviewEntries(preview.included);
    const excluded = this.sortPreviewEntries(preview.excluded);
    const colorMap = this.buildSitemapColorMap([...included, ...excluded]);

    const renderRow = (entry: CrawlUrlPreviewEntry): string => {
      const sm = entry.sourceSitemapUrl || '';
      const colors = colorMap.get(sm) || { bg: '#f5f5f5', fg: '#1a1a1a' };
      const label = this.displaySitemapLabel(sm);
      const safeUrl = this.escapeHtml(entry.url);
      const reason = entry.excludedReason
        ? `<div class="reason">${this.escapeHtml(entry.excludedReason)}</div>`
        : '';
      return `<tr style="background:${colors.bg};color:${colors.fg}">
        <td class="url"><a href="${safeUrl}" target="_blank" rel="noopener">${safeUrl}</a>${reason}</td>
        <td class="sitemap" title="${this.escapeHtml(sm)}">${this.escapeHtml(label)}</td>
      </tr>`;
    };

    const legend = [...colorMap.keys()].map((sm) => {
      const colors = colorMap.get(sm)!;
      const label = this.displaySitemapLabel(sm);
      return `<span class="legend-item" style="background:${colors.bg}">${this.escapeHtml(label)}</span>`;
    }).join('');

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <title>Crawl URL preview — ${this.escapeHtml(domain)}</title>
  <style>
    body { font-family: system-ui, sans-serif; margin: 1.5rem; color: #1a1a1a; }
    h1 { font-size: 1.25rem; margin: 0 0 0.25rem; }
    .meta { color: #555; margin-bottom: 1rem; font-size: 0.9rem; }
    .legend { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-bottom: 1.25rem; }
    .legend-item { padding: 0.2rem 0.5rem; border-radius: 4px; font-size: 0.8rem; border: 1px solid rgba(0,0,0,0.08); }
    .columns { display: grid; grid-template-columns: 1fr 1fr; gap: 1.5rem; align-items: start; }
    @media (max-width: 900px) { .columns { grid-template-columns: 1fr; } }
    h2 { font-size: 1rem; margin: 0 0 0.5rem; }
    table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
    th, td { padding: 0.35rem 0.5rem; text-align: left; vertical-align: top; border-bottom: 1px solid rgba(0,0,0,0.06); }
    th { font-weight: 600; background: #fafafa; position: sticky; top: 0; }
    .url a { color: inherit; word-break: break-all; }
    .url .reason { margin-top: 2px; font-size: 0.72rem; opacity: 0.7; }
    .sitemap { width: 28%; font-size: 0.75rem; opacity: 0.85; word-break: break-all; }
    .empty { color: #888; font-style: italic; padding: 0.5rem 0; }
  </style>
</head>
<body>
  <h1>Crawl URL preview — ${this.escapeHtml(domain)}</h1>
  <p class="meta">Will crawl: ${included.length} · Won't be crawled: ${excluded.length} · Sorted by sitemap</p>
  <div class="legend">${legend}</div>
  <div class="columns">
    <section>
      <h2>Will be crawled (${included.length})</h2>
      ${included.length ? `<table>
        <thead><tr><th>URL</th><th>Sitemap</th></tr></thead>
        <tbody>${included.map(renderRow).join('')}</tbody>
      </table>` : '<p class="empty">None</p>'}
    </section>
    <section>
      <h2>Won't be crawled (${excluded.length})</h2>
      ${excluded.length ? `<table>
        <thead><tr><th>URL</th><th>Sitemap</th></tr></thead>
        <tbody>${excluded.map(renderRow).join('')}</tbody>
      </table>` : '<p class="empty">None</p>'}
    </section>
  </div>
</body>
</html>`;
  }

  openUrlListPreview(): void {
    const domain = this.normalizeDomain(this.newDomainInput) || 'domain';
    this.fetchCrawlUrlData()
      .then(({ preview }) => {
        const html = this.buildCrawlUrlPreviewHtml(domain, preview);
        const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      })
      .catch((err) => {
        this.notifications.error(err?.error?.error || err?.error?.message || err?.message || 'Could not load URL list');
      });
  }

  downloadUrlList(): void {
    const domain = this.normalizeDomain(this.newDomainInput) || 'crawl-urls';
    this.fetchCrawlUrlData()
      .then(({ urlsToCrawl }) => {
        const blob = new Blob([urlsToCrawl.join('\n') + '\n'], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `${domain.replace(/[^a-z0-9.-]+/gi, '-')}-urls.txt`;
        anchor.click();
        URL.revokeObjectURL(url);
      })
      .catch((err) => {
        this.notifications.error(err?.error?.error || err?.error?.message || err?.message || 'Could not download URL list');
      });
  }

  private applyEstimateResponse(data: CrawlEstimate, preserveSelection: boolean, forDomain?: string): void {
    this.estimate = data;
    if (forDomain) this.checkedDomain = forDomain;
    this.localePartitionedSitemaps = !!data.localePartitionedSitemaps;
    this.availableLeafSitemaps = this.collectLeafSitemaps(data?.sitemapTree || []);
    const validLeafSet = new Set(this.availableLeafSitemaps.map((e) => e.sitemapUrl));
    this.perSitemapLimits = Object.fromEntries(
      Object.entries(this.perSitemapLimits).filter(([url, v]) => validLeafSet.has(url) && v > 0)
    );

    this.syncNeverTrackSelection(preserveSelection);

    this.availableLocaleGroups = data?.localeGroups || [];
    const validLocaleSet = new Set(this.availableLocaleGroups.map((g) => g.key));
    if (preserveSelection) {
      this.selectedLocales = new Set(
        Array.from(this.selectedLocales).filter((key) => validLocaleSet.has(key))
      );
    } else if (this.availableLocaleGroups.length > 0) {
      this.selectedLocales = new Set(
        validLocaleSet.has('__default__') ? ['__default__'] : []
      );
    } else {
      this.selectedLocales.clear();
    }

    if (preserveSelection) {
      this.selectedSitemaps = new Set(
        Array.from(this.selectedSitemaps).filter((url) => validLeafSet.has(url))
      );
    } else if (this.localePartitionedSitemaps) {
      this.syncSitemapSelectionToLocales();
    } else {
      this.selectedSitemaps = new Set(this.availableLeafSitemaps.map((e) => e.sitemapUrl));
    }

    if (!preserveSelection && this.availableLocaleGroups.length > 0 && this.selectedLocales.size > 0) {
      if (this.usingUploadedSitemaps) {
        this.estimateFromUploadedSitemaps(true);
      } else {
        this.estimateNewDomain(true);
      }
      return;
    }
    this.estimateLoading = false;
  }

  // --- FW-ONB-01 never-track suggestions (SS001-19) ------------------------

  /**
   * The server already applied its own defaults to the estimate we just got
   * back, so a fresh check needs no second round-trip to re-scope. A re-estimate
   * only has to forget choices for groups that are no longer on offer.
   */
  private syncNeverTrackSelection(preserveSelection: boolean): void {
    this.neverTrackChoices = preserveSelection
      ? pruneNeverTrackOverrides(this.neverTrackGroups, this.neverTrackChoices)
      : new Map();
  }

  get neverTrackGroups(): NeverTrackGroup[] {
    return this.estimate?.neverTrackGroups || [];
  }

  /** Suggested groups the operator ticked back on. */
  get keepNeverTrackKeys(): string[] {
    return neverTrackOverrides(this.neverTrackGroups, this.neverTrackChoices).keepNeverTrackKeys;
  }

  /** Guard-spared groups the operator excluded anyway. */
  get excludeNeverTrackKeys(): string[] {
    return neverTrackOverrides(this.neverTrackGroups, this.neverTrackChoices).excludeNeverTrackKeys;
  }

  isNeverTrackTracked(group: NeverTrackGroup): boolean {
    return isNeverTrackGroupTracked(group, this.neverTrackChoices);
  }

  toggleNeverTrackGroup(group: NeverTrackGroup, tracked: boolean): void {
    this.neverTrackChoices.set(group.key, tracked);
    this.refreshEstimateForFilters();
  }

  setAllNeverTrackTracked(tracked: boolean): void {
    this.neverTrackChoices = setAllNeverTrackOverrides(this.neverTrackGroups, tracked);
    this.refreshEstimateForFilters();
  }

  /** URLs the current selection drops — the number the estimate was computed with. */
  get neverTrackExcludedCount(): number {
    return this.estimate?.neverTrackExcludedCount ?? 0;
  }

  neverTrackGroupLabel(group: NeverTrackGroup): string {
    return group.kind === 'folder' ? `${group.path}/ — whole folder` : group.path;
  }

  trackNeverTrackGroup(_index: number, group: NeverTrackGroup): string {
    return group.key;
  }

  private syncSitemapSelectionToLocales(): void {
    if (!this.localePartitionedSitemaps) return;
    const leaves = this.getLeafNodes(this.estimate?.sitemapTree);
    this.selectedSitemaps = new Set(
      leaves
        .filter((leaf) => {
          const key = leaf.dominantLocaleKey || '__default__';
          return this.selectedLocales.has(key);
        })
        .map((leaf) => leaf.sitemapUrl)
    );
  }

  private handleEstimateError(err: unknown, forDomain?: string): void {
    this.estimate = null;
    if (forDomain) this.checkedDomain = forDomain;
    const payload = (err as { error?: { error?: string; message?: string; code?: string } })?.error;
    const code = payload?.code || null;
    const message = payload?.error || payload?.message || (err as { message?: string })?.message || 'Failed to read sitemap for that domain.';
    if (code === 'BOT_PROTECTED') {
      this.botProtected = true;
      this.estimateError = null;
    } else {
      this.botProtected = false;
      this.estimateError = message;
    }
    this.estimateLoading = false;
  }

  get uploadedSitemapFileNames(): string {
    return this.uploadedSitemapFiles.map((f) => f.name).join(', ');
  }

  displaySitemapLabel(sitemapUrl: string): string {
    return sitemapUrl.startsWith('upload://') ? sitemapUrl.slice('upload://'.length) : sitemapUrl;
  }

  private async readUploadedSitemapPayload(): Promise<Array<{ filename: string; content: string }>> {
    const files = await Promise.all(
      this.uploadedSitemapFiles.map(async (file) => ({
        filename: file.name,
        content: await file.text()
      }))
    );
    return files;
  }

  trackSitemap(_index: number, node: SitemapTreeNode): string {
    return node.sitemapUrl;
  }

  /** True when the last sitemap check was for the domain currently typed in. */
  private get checkMatchesInput(): boolean {
    const typed = this.normalizeDomain(this.newDomainInput);
    return Boolean(typed) && this.checkedDomain === typed;
  }

  /**
   * Why Start crawl is disabled. The sitemap estimate is a prerequisite, not a
   * suggestion: without it the operator can't see how many pages (or dollars)
   * the crawl is about to spend (SS001-2).
   */
  get startCrawlBlockedReason(): string | null {
    return addDomainStartCrawlBlockedReason({
      domainInput: this.newDomainInput,
      startingCrawl: this.startingCrawl,
      estimateLoading: this.estimateLoading,
      hasEstimate: Boolean(this.estimate) && this.checkMatchesInput,
      checkFailed: Boolean(this.estimateError || this.botProtected) && this.checkMatchesInput,
      leafSitemapCount: this.availableLeafSitemaps.length,
      selectedSitemapCount: this.selectedSitemaps.size,
      localeGroupCount: this.availableLocaleGroups.length,
      selectedLocaleCount: this.selectedLocales.size
    });
  }

  startNewDomainCrawl(): void {
    const domain = this.normalizeDomain(this.newDomainInput);
    const blocked = this.startCrawlBlockedReason;
    if (blocked) {
      if (!this.startingCrawl) this.notifications.error(blocked);
      return;
    }
    if (!domain) return;
    const asCompetitor = this.addMode === 'competitor';
    const focalDomain = this.domain;
    this.startingCrawl = true;

    const start = (uploadedSitemaps: Array<{ filename: string; content: string }> = []) => {
      this.crawl
        .startCrawl(domain, {
          limit: this.getEffectiveCrawlLimit(),
          selectedSitemaps: Array.from(this.selectedSitemaps),
          selectedLocales: Array.from(this.selectedLocales),
          minLastmodFrom: this.minLastmodFrom || null,
          perSitemapLimits: this.perSitemapLimits,
          perSitemapLimitMode: this.perSitemapLimitMode,
          keepNeverTrackKeys: this.keepNeverTrackKeys,
          excludeNeverTrackKeys: this.excludeNeverTrackKeys,
          uploadedSitemaps,
          crawlMode: this.isDevView() && this.usePerUrlFallbackCrawl ? 'curated' : 'sitemap'
        })
        .subscribe({
          next: (res) => {
            this.startingCrawl = false;
            this.showAddDomain = false;
            this.newDomainInput = '';
            this.estimate = null;
            this.checkedDomain = null;
            this.availableLeafSitemaps = [];
            this.selectedSitemaps.clear();
            this.availableLocaleGroups = [];
            this.selectedLocales.clear();
            this.neverTrackChoices.clear();
            this.botProtected = false;
            this.uploadedSitemapFiles = [];
            this.usingUploadedSitemaps = false;
            const canonical = (res.canonicalDomain || domain).toLowerCase().replace(/^www\./, '');

            if (asCompetitor && focalDomain) {
              this.notifications.success(
                res?.message || `Crawl started for ${canonical}. It will be added as a competitor of ${focalDomain}.`
              );
              this.compareContext.addMarkedCompetitor(canonical, focalDomain).subscribe({
                next: () => {
                  this.domainContext.refresh(focalDomain);
                  this.loadAll(false);
                },
                error: (err) =>
                  this.notifications.error(
                    err?.error?.error || err?.error?.message || err?.message || 'Crawl started but failed to mark as competitor.'
                  )
              });
            } else {
              this.notifications.success(res?.message || `Crawl started for ${canonical}. Finish setup once it completes.`);
              this.domainContext.refresh(canonical);
              this.selectSetupDomain(canonical);
            }
          },
          error: (err) => {
            this.startingCrawl = false;
            this.notifications.error(err?.error?.error || err?.error?.message || err?.message || 'Failed to start crawl.');
          }
        });
    };

    if (this.usingUploadedSitemaps && this.uploadedSitemapFiles.length > 0) {
      this.readUploadedSitemapPayload()
        .then((uploadedSitemaps) => start(uploadedSitemaps))
        .catch(() => {
          this.startingCrawl = false;
          this.notifications.error('Could not read uploaded sitemap files.');
        });
      return;
    }
    start();
  }

  // --- Sitemap scoping helpers (mirror Crawl Management) ------------------

  private collectLeafSitemaps(nodes: SitemapTreeNode[]): Array<{ sitemapUrl: string; urlCount: number }> {
    const leaves: Array<{ sitemapUrl: string; urlCount: number }> = [];
    const walk = (node: SitemapTreeNode) => {
      if (node.type === 'urlset') leaves.push({ sitemapUrl: node.sitemapUrl, urlCount: node.urlCount || 0 });
      (node.children || []).forEach(walk);
    };
    nodes.forEach(walk);
    return leaves;
  }

  getLeafNodes(nodes: SitemapTreeNode[] | undefined): SitemapTreeNode[] {
    if (!nodes || nodes.length === 0) return [];
    const leaves: SitemapTreeNode[] = [];
    const walk = (node: SitemapTreeNode) => {
      if (node.type === 'urlset') leaves.push(node);
      (node.children || []).forEach(walk);
    };
    nodes.forEach(walk);
    return leaves;
  }

  getLeafDisplayUrlCount(node: SitemapTreeNode): number {
    const base = this.minLastmodFrom ? (node.filteredUrlCount ?? 0) : (node.urlCount || 0);
    const cap = this.getPerSitemapLimit(node.sitemapUrl);
    return cap ? Math.min(base, cap) : base;
  }

  getPerSitemapLimit(sitemapUrl: string): number | null {
    const value = this.perSitemapLimits[sitemapUrl];
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  onPerSitemapLimitChange(sitemapUrl: string, value: string | number | null): void {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) {
      this.perSitemapLimits[sitemapUrl] = Math.floor(parsed);
    } else {
      delete this.perSitemapLimits[sitemapUrl];
    }
    this.refreshEstimateForFilters();
  }

  onMinLastmodFromChange(value: string): void {
    this.minLastmodFrom = value;
    this.refreshEstimateForFilters();
  }

  onPerSitemapLimitModeChange(value: 'recent' | 'oldest' | 'balanced'): void {
    this.perSitemapLimitMode = value;
    this.refreshEstimateForFilters();
  }

  toggleSitemapSelection(sitemapUrl: string, checked: boolean): void {
    if (checked) {
      this.selectedSitemaps.add(sitemapUrl);
    } else {
      this.selectedSitemaps.delete(sitemapUrl);
    }
    this.refreshEstimateForFilters();
  }

  setAllSitemapsSelected(checked: boolean): void {
    if (checked) {
      this.selectedSitemaps = new Set(this.availableLeafSitemaps.map((e) => e.sitemapUrl));
    } else {
      this.selectedSitemaps.clear();
    }
    this.refreshEstimateForFilters();
  }

  areAllSitemapsSelected(): boolean {
    return this.availableLeafSitemaps.length > 0 && this.selectedSitemaps.size === this.availableLeafSitemaps.length;
  }

  hasSitemapSelection(): boolean {
    return this.selectedSitemaps.size > 0;
  }

  // --- Locale scoping helpers --------------------------------------------

  toggleLocaleSelection(key: string, checked: boolean): void {
    if (checked) {
      this.selectedLocales.add(key);
    } else {
      this.selectedLocales.delete(key);
    }
    if (this.localePartitionedSitemaps) {
      this.syncSitemapSelectionToLocales();
    }
    this.refreshEstimateForFilters();
  }

  setAllLocalesSelected(checked: boolean): void {
    if (checked) {
      this.selectedLocales = new Set(this.availableLocaleGroups.map((g) => g.key));
    } else {
      this.selectedLocales.clear();
    }
    if (this.localePartitionedSitemaps) {
      this.syncSitemapSelectionToLocales();
    }
    this.refreshEstimateForFilters();
  }

  areAllLocalesSelected(): boolean {
    return this.availableLocaleGroups.length > 0 && this.selectedLocales.size === this.availableLocaleGroups.length;
  }

  trackLocaleGroup(_index: number, group: LocaleGroup): string {
    return group.key;
  }

  getSelectedDiscoveredUrlCount(): number {
    return this.estimate?.selectedPagesInSitemap ?? 0;
  }

  getSelectedIncludedAtCap(): number {
    return this.estimate?.selectedFilteredPages ?? 0;
  }

  getSelectedEstimatedCost(): number {
    if (!this.estimate) return 0;
    return this.estimate.selectedEstimatedTotalCost ?? (this.getSelectedIncludedAtCap() * this.estimate.estimatedCostPerPage);
  }

  private refreshEstimateForFilters(): void {
    if (!this.estimate) return;
    if (this.estimateDebounce) clearTimeout(this.estimateDebounce);
    this.estimateDebounce = setTimeout(() => {
      this.estimateDebounce = null;
      if (this.usingUploadedSitemaps) {
        this.estimateFromUploadedSitemaps(true);
      } else {
        this.estimateNewDomain(true);
      }
    }, 600);
  }

  // --- Display helpers ----------------------------------------------------

  stateLabel(state: SetupStepState): string {
    switch (state) {
      case 'no_data':
        return 'No data';
      case 'not_started':
        return 'Not started';
      case 'waiting':
        return 'Waiting for crawl';
      case 'queued':
        return 'Queued';
      case 'running':
        return 'Running';
      case 'partial':
        return 'Partial';
      case 'complete':
        return 'Complete';
      case 'blocked':
        return 'Blocked';
      case 'failed':
        return 'Failed';
      default:
        return state;
    }
  }

  stateClass(state: SetupStepState): string {
    return `state-${state}`;
  }

  /** Plain-language blocker for Supporting page opportunities (always shown when blocked). */
  opportunitiesBlockedSummary(step: SetupStep): string | null {
    if (step.key !== 'supporting-opportunities' || step.state !== 'blocked') return null;
    const opp = this.status?.supportingPagesOpportunities;
    const byTopic = this.status?.supportingPagesByTopic;

    if (opp?.contentGapAvailable === false) {
      return 'Start the content-gap sidecar in a terminal: npm run content-gap:serve — then click Refresh at the top of Setup and Run here.';
    }
    if (byTopic?.status !== 'complete' && opp?.supportingPagesReady === false) {
      return 'Run Supporting pages by topic in the Pipeline section first (above).';
    }
    if (step.blockedReason?.trim()) {
      return step.blockedReason.trim();
    }
    return 'Click Refresh at the top of Setup. If still blocked, restart the backend dev server (the sidecar health check may be stale).';
  }

  /** True when the only fix is starting/retrying the content-gap sidecar. */
  isSidecarBlocked(step: SetupStep): boolean {
    if (step.key !== 'supporting-opportunities') return false;
    const opp = this.status?.supportingPagesOpportunities;
    if (opp?.contentGapAvailable === false) return true;
    const reason = (step.blockedReason || '').toLowerCase();
    return step.state === 'blocked' && (reason.includes('sidecar') || reason.includes('content-gap') || reason.includes('content gap'));
  }

  opportunitiesByTopicMissing(step: SetupStep): boolean {
    if (step.key !== 'supporting-opportunities' || step.state !== 'blocked') return false;
    const opp = this.status?.supportingPagesOpportunities;
    const byTopic = this.status?.supportingPagesByTopic;
    if (byTopic?.status === 'complete') return false;
    return opp?.supportingPagesReady === false && !this.isSidecarBlocked(step);
  }
}
